import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';
import { api, resolveApiMediaUrl } from '../api/client.js';
import { useTenant } from '../context/TenantContext.jsx';
import { isReservedStoreSlug } from '../config/reservedSlugs.js';
import { STORE_TIMEZONE_IANA_SUGGESTIONS } from '../config/storeTimezones.js';
import { toSlug } from '../utils/slug.js';
import {
  parseStoreAccent,
  STORE_ACCENT_KEYS,
  STORE_ACCENT_LABELS,
  STORE_ACCENT_SWATCHES,
} from '../theme/storeAccent.js';
import {
  normalizeThemeJson,
  parseStoreHomeLayout,
  storeHomeLayoutBodyForEditor,
  STORE_HOME_LAYOUT_EDITOR_EXAMPLE,
} from '../theme/storeHomeLayout.js';
import { AdminTenantReportModal } from '../components/admin/AdminTenantReportModal.jsx';
import { AdminDeliveryZonesSection } from '../components/admin/AdminDeliveryZonesSection.jsx';
import { StoreVitrineLayoutForm } from '../components/empresa/StoreVitrineLayoutForm.jsx';
import { StoreHeroForm } from '../components/empresa/StoreHeroForm.jsx';
import {
  parseStoreHero,
  storeHeroForSave,
  STORE_HERO_AUTOPLAY_DEFAULT,
} from '../theme/storeHero.js';
import { StoreCatalogLuxeForm } from '../components/empresa/StoreCatalogLuxeForm.jsx';
import {
  parseStoreCatalogLuxe,
  storeCatalogLuxeBodyForEditor,
  STORE_CATALOG_LUXE_EDITOR_EXAMPLE,
} from '../theme/storeCatalogLuxe.js';
import {
  parseStoreCatalogList,
  storeCatalogListBodyForEditor,
} from '../theme/storeCatalogList.js';
import {
  parseStoreAnalytics,
  storeAnalyticsBodyForEditor,
} from '../theme/storeAnalytics.js';
import {
  storeDocumentOrderBodyForEditor,
  storeDocumentOrderPatchForSave,
} from '../theme/storeDocumentOrder.js';
import { StoreCatalogListForm } from '../components/empresa/StoreCatalogListForm.jsx';
import { StoreAnalyticsForm } from '../components/empresa/StoreAnalyticsForm.jsx';
import { StoreDocumentOrderForm } from '../components/empresa/StoreDocumentOrderForm.jsx';
import { storeProductDisplayBodyForEditor } from '../theme/storeProductDisplay.js';
import { StoreProductDisplayForm } from '../components/empresa/StoreProductDisplayForm.jsx';

/**
 * Base da integração ERP: termina em …/index.php/api — o ERP concatena /categorias, /produtos, etc.
 * Override: `VITE_ERP_INTEGRATION_URL` (sem barra no fim).
 */
function formatBrlPretty(n) {
  if (n == null || n === '' || Number.isNaN(Number(n))) return '—';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(n));
}

function buildErpIntegrationBaseUrl() {
  const raw = typeof import.meta.env?.VITE_ERP_INTEGRATION_URL === 'string'
    ? import.meta.env.VITE_ERP_INTEGRATION_URL.trim()
    : '';
  if (raw) return raw.replace(/\/?$/, '');
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin.replace(/\/?$/, '')}/integration-php/public/index.php/api`;
  }
  return '/integration-php/public/index.php/api';
}

/** Converte flags 0/1 da API (número, string ou boolean) para boolean — evita `Number(true)` → NaN e diffs errados ao gravar. */
function tenantFlagFromApi(v) {
  if (v === true || v === 1) return true;
  if (v === false || v === 0 || v == null || v === '') return false;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (['1', 'true', 'yes', 'on'].includes(s)) return true;
    if (['0', 'false', 'no', 'off'].includes(s)) return false;
    const n = Number(s.replace(',', '.'));
    return Number.isFinite(n) && n !== 0;
  }
  const n = Number(v);
  return Number.isFinite(n) && n !== 0;
}

/** AAAA-MM-DD → DD/MM/AAAA */
function formatYmdPt(ymd) {
  const s = typeof ymd === 'string' ? ymd.trim() : '';
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return s || '—';
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function CheckIcon({ done }) {
  return done ? (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-700 dark:text-emerald-300" aria-hidden>
      <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M2 6l3 3 5-6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  ) : (
    <span
      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-ink/15 bg-ink/[0.03] text-transparent dark:border-white/15 dark:bg-white/[0.04]"
      aria-hidden
    >
      <span className="h-1.5 w-1.5 rounded-full bg-ink/20 dark:bg-white/25" />
    </span>
  );
}

/**
 * Checklist de clareza no topo do modal (estado gravado + rascunho actual).
 * @param {{
 *   editModal: Record<string, unknown>,
 *   editNome: string,
 *   editSlug: string,
 *   logoUrlInput: string,
 *   logoFile: File | null,
 *   editContactAddress: string,
 *   editContactWhatsapp: string,
 *   editContactMaps: string,
 *   editStoreHomeEnabled: boolean,
 *   editCatalogLuxeEnabled: boolean,
 *   editCatalogListEnabled: boolean,
 *   editPaymentOnline: boolean,
 *   editErpClientId: string,
 *   canSaveErp: boolean,
 * }} props
 */
function TenantModalSetupChecklist({
  editModal,
  editNome,
  editSlug,
  logoUrlInput,
  logoFile,
  editContactAddress,
  editContactWhatsapp,
  editContactMaps,
  editStoreHomeEnabled,
  editCatalogLuxeEnabled,
  editCatalogListEnabled,
  editPaymentOnline,
  editErpClientId,
  canSaveErp,
}) {
  const nomeOk = editNome.trim().length >= 2;
  const slugNorm = toSlug(editSlug.trim());
  const slugOk = Boolean(slugNorm && editSlug.trim());
  const logoOk = Boolean(
    logoFile || (logoUrlInput && logoUrlInput.trim()) || (editModal?.logo_url && String(editModal.logo_url).trim()),
  );
  const contactOk = [editContactAddress, editContactWhatsapp, editContactMaps].some((x) => String(x || '').trim());
  const erpOk =
    Boolean(editErpClientId && editErpClientId.trim()) ||
    Boolean(editModal?.erp_client_id && String(editModal.erp_client_id).trim());

  const items = [
    { ok: nomeOk && slugOk, label: 'Nome e endereço público (slug) prontos para a loja' },
    { ok: logoOk, label: 'Logo ou URL de imagem (opcional, recomendado)' },
    { ok: contactOk, label: 'Dados de contacto na ficha da loja (opcional)' },
    { ok: editStoreHomeEnabled, label: 'Vitrine modular activa no topo da loja' },
    { ok: editCatalogLuxeEnabled, label: 'Catálogo luxo (apresentação editorial)' },
    { ok: editCatalogListEnabled, label: 'Formato lista (produtos em linhas)' },
    { ok: editPaymentOnline, label: 'Pagamento online (Mercado Pago) ligado' },
  ];
  if (canSaveErp) {
    items.push({ ok: erpOk, label: 'Credenciais ERP (Client ID) para o sincronizador' });
  }

  const doneCount = items.filter((i) => i.ok).length;

  return (
    <div
      className="rounded-2xl border border-emerald-600/25 bg-emerald-500/[0.06] px-3 py-3 dark:border-emerald-500/20 dark:bg-emerald-950/20"
      role="region"
      aria-labelledby="tenant-checklist-heading"
    >
      <div className="flex items-baseline justify-between gap-2">
        <p id="tenant-checklist-heading" className="text-[11px] font-bold uppercase tracking-wide text-emerald-900 dark:text-emerald-100">
          Estado desta empresa
        </p>
        <span className="text-[10px] font-semibold tabular-nums text-emerald-800/90 dark:text-emerald-200/90">
          {doneCount}/{items.length}
        </span>
      </div>
      <ul className="mt-2 space-y-2" aria-live="polite">
        {items.map((it) => (
          <li key={it.label} className="flex gap-2.5 text-[11px] leading-snug text-ink/85 dark:text-mist/85">
            <CheckIcon done={it.ok} />
            <span className={it.ok ? 'text-ink dark:text-mist' : 'text-ink/55 dark:text-mist/70'}>{it.label}</span>
          </li>
        ))}
      </ul>
      <p
        id="tenant-edit-checklist-hint"
        className="mt-2 border-t border-emerald-600/15 pt-2 text-[10px] leading-relaxed text-ink/55 dark:border-emerald-500/20 dark:text-mist/72"
      >
        Itens opcionais ajudam a loja a parecer completa; os obrigatórios para publicar são nome e slug. Ajuste as abas
        em baixo e grave no fim.
      </p>
    </div>
  );
}

export function TenantsPage() {
  const { user } = useOutletContext() || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const canSaveErp = Boolean(user?.has_erp_api || user?.role === 'super_admin');
  const { setTenantId, tenantId } = useTenant();
  const [tenants, setTenants] = useState([]);
  const [nome, setNome] = useState('');
  const [slug, setSlug] = useState('');
  const [tokenPlain, setTokenPlain] = useState('');
  const [tokenNote, setTokenNote] = useState('');
  /** Mostrados uma vez após criar empresa (plano Ouro): não voltam na listagem GET */
  const [erpClientIdOnce, setErpClientIdOnce] = useState('');
  const [erpClientSecretOnce, setErpClientSecretOnce] = useState('');
  const [newShopUrl, setNewShopUrl] = useState('');
  const [copyOk, setCopyOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [editModal, setEditModal] = useState(null);
  const [reportTenant, setReportTenant] = useState(null);
  const [editNome, setEditNome] = useState('');
  const [editSlug, setEditSlug] = useState('');
  const [logoUrlInput, setLogoUrlInput] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState('');
  const [editErpClientId, setEditErpClientId] = useState('');
  const [editErpClientSecret, setEditErpClientSecret] = useState('');
  const [editStoreAccent, setEditStoreAccent] = useState('green');
  const [editHidePricesRequireLogin, setEditHidePricesRequireLogin] = useState(false);
  const [editBlockCustomerPublicRegistration, setEditBlockCustomerPublicRegistration] = useState(false);
  const [editRequireCustomerEmail, setEditRequireCustomerEmail] = useState(true);
  const [editRequireCustomerWhatsapp, setEditRequireCustomerWhatsapp] = useState(false);
  const [editRequireAdultConfirmation, setEditRequireAdultConfirmation] = useState(false);
  const [editCatalogOnlyMode, setEditCatalogOnlyMode] = useState(false);
  const [editCatalogNoPhotoMode, setEditCatalogNoPhotoMode] = useState(false);
  const [editBlockSaleZeroStock, setEditBlockSaleZeroStock] = useState(false);
  const [editPickupOnlyMode, setEditPickupOnlyMode] = useState(false);
  const [editPaymentOnline, setEditPaymentOnline] = useState(false);
  const [editMpAccessToken, setEditMpAccessToken] = useState('');
  const [editContactAddress, setEditContactAddress] = useState('');
  const [editContactWhatsapp, setEditContactWhatsapp] = useState('');
  const [editContactMaps, setEditContactMaps] = useState('');
  const [editContactFacebook, setEditContactFacebook] = useState('');
  const [editContactInstagram, setEditContactInstagram] = useState('');
  const [editContactYoutube, setEditContactYoutube] = useState('');
  const [editContactHours, setEditContactHours] = useState('');
  const [editTimezone, setEditTimezone] = useState('');
  /** Uma forma de pagamento por linha — a API normaliza (maiúsculas, sem acentos). */
  const [editCheckoutPaymentLines, setEditCheckoutPaymentLines] = useState('');
  /** Texto customizado da opção que dispara o Mercado Pago no checkout (vazio = padrão). */
  const [editCheckoutOnlineLabel, setEditCheckoutOnlineLabel] = useState('');
  /** ID TPV (máx. 3 dígitos) — enviado ao ERP em `tpv_Id` nos pedidos. */
  const [editTpvId, setEditTpvId] = useState('');
  const [discountCoupons, setDiscountCoupons] = useState([]);
  const [newCouponCode, setNewCouponCode] = useState('');
  const [newCouponPct, setNewCouponPct] = useState('10');
  /** @type {'single'|'multi'} */
  const [newCouponKind, setNewCouponKind] = useState('single');
  /** AAAA-MM-DD para campanha */
  const [newCouponValidUntil, setNewCouponValidUntil] = useState('');
  /** vazio no multi = uso ilimitado até à data */
  const [newCouponMaxUses, setNewCouponMaxUses] = useState('');
  const [couponSaveBusy, setCouponSaveBusy] = useState(false);
  const [paySubBusyId, setPaySubBusyId] = useState(null);
  const [billingNotice, setBillingNotice] = useState('');
  const [ownerWhatsapp, setOwnerWhatsapp] = useState('');
  const [ownerWhatsappBusy, setOwnerWhatsappBusy] = useState(false);
  const [ownerWhatsappMsg, setOwnerWhatsappMsg] = useState('');
  const [editWaShToken, setEditWaShToken] = useState('');
  const [clearWaShToken, setClearWaShToken] = useState(false);
  /** @type {{ source: string, endpoint_present: boolean, token_present: boolean, ready: boolean } | null} */
  const [waStatus, setWaStatus] = useState(null);
  /** @type {'dados' | 'vitrine_inicial' | 'catalogo_luxe' | 'formato_lista'} */
  const [editTenantModalTab, setEditTenantModalTab] = useState('dados');
  const [editStoreHomeEnabled, setEditStoreHomeEnabled] = useState(false);
  const [editStoreHomeLayoutJson, setEditStoreHomeLayoutJson] = useState('{}');
  const [editStoreHeroEnabled, setEditStoreHeroEnabled] = useState(false);
  const [editStoreHeroAutoplay, setEditStoreHeroAutoplay] = useState(STORE_HERO_AUTOPLAY_DEFAULT);
  const [editStoreHeroSlides, setEditStoreHeroSlides] = useState([]);
  const [editCatalogLuxeEnabled, setEditCatalogLuxeEnabled] = useState(false);
  const [editCatalogLuxeJson, setEditCatalogLuxeJson] = useState('{}');
  const [editCatalogListEnabled, setEditCatalogListEnabled] = useState(false);
  const [editCatalogListJson, setEditCatalogListJson] = useState('{}');
  const [editStoreProductDisplayJson, setEditStoreProductDisplayJson] = useState('{}');
  const [editGa4MeasurementId, setEditGa4MeasurementId] = useState('');
  const [editMetaPixelId, setEditMetaPixelId] = useState('');
  const [editDocumentOrderEnabled, setEditDocumentOrderEnabled] = useState(false);
  const [editOpenAiKey, setEditOpenAiKey] = useState('');
  const [editAnthropicApiKey, setEditAnthropicApiKey] = useState('');
  const [editOpenAiConfigured, setEditOpenAiConfigured] = useState(false);
  const [editAnthropicConfigured, setEditAnthropicConfigured] = useState(false);

  const editTenantModalTabRef = useRef(editTenantModalTab);
  editTenantModalTabRef.current = editTenantModalTab;
  const editModalDadosTabRef = useRef(null);
  const editModalVitrineTabRef = useRef(null);
  /** Elemento que abriu o modal de configurações — foco devolvido ao fechar. */
  const editModalFocusReturnRef = useRef(null);
  /** Booleanos da loja tal como ao abrir o modal — comparação ao gravar não depende de `editModal` ter sido actualizado no meio da edição. */
  const tenantEditBooleanBaselineRef = useRef(null);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  let shopHost = '';
  if (origin) {
    try {
      shopHost = new URL(origin).host;
    } catch {
      shopHost = origin;
    }
  }

  async function reload() {
    const data = await api('/tenants');
    setTenants(data.tenants || []);
  }

  async function payOpenMonthlyStore(rowId) {
    setPaySubBusyId(rowId);
    setError('');
    try {
      const res = await api(`/tenants/${rowId}/subscription/pay-open`, { method: 'POST' });
      const url = res?.init_point;
      if (!url || typeof url !== 'string') {
        setError('Resposta da API sem URL de checkout Mercado Pago.');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err.message || 'Não foi possível iniciar o pagamento.');
    } finally {
      setPaySubBusyId(null);
    }
  }

  useEffect(() => {
    reload().catch(() => {});
  }, []);

  useEffect(() => {
    const b = searchParams.get('billing');
    const tenantPay = searchParams.get('tenant');

    if (b === 'pay' && tenantPay) {
      const rowId = Number(tenantPay);
      if (Number.isFinite(rowId) && rowId > 0 && tenants.some((t) => Number(t.id) === rowId)) {
        const next = new URLSearchParams(searchParams);
        next.delete('billing');
        next.delete('tenant');
        setSearchParams(next, { replace: true });
        payOpenMonthlyStore(rowId);
      }
      return;
    }

    if (!b) return;
    if (b === 'paid') {
      setBillingNotice(
        'Pagamento registado — se o webhook do Mercado Pago já processou, a loja será desbloqueada em poucos instantes.',
      );
    } else if (b === 'failure') {
      setBillingNotice('Pagamento não concluído. Pode tentar novamente.');
    } else if (b === 'pending') {
      setBillingNotice('Pagamento pendente na MP. Quando confirmar, a loja será regularizada.');
    }
    const next = new URLSearchParams(searchParams);
    next.delete('billing');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- payOpenMonthlyStore estável o suficiente para deep-link de cobrança
  }, [searchParams, setSearchParams, tenants]);

  useEffect(() => {
    if (!editModal?.id) return;
    reloadDiscountCoupons(editModal.id).catch(() => {});
  }, [editModal?.id]);

  useEffect(() => {
    setOwnerWhatsapp(String(user?.whatsapp || ''));
  }, [user?.whatsapp]);

  /** Foco inicial na aba correcta ao abrir o modal (não repete ao mudar de aba). */
  useEffect(() => {
    if (!editModal?.id) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const tab = editTenantModalTabRef.current;
      if (tab === 'vitrine_inicial') {
        editModalVitrineTabRef.current?.focus();
      } else {
        editModalDadosTabRef.current?.focus();
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [editModal?.id]);

  const closeEditModal = useCallback(() => {
    const back = editModalFocusReturnRef.current;
    editModalFocusReturnRef.current = null;
    tenantEditBooleanBaselineRef.current = null;
    setEditModal(null);
    setEditError('');
    if (back instanceof HTMLElement && typeof document !== 'undefined' && document.body.contains(back)) {
      window.requestAnimationFrame(() => {
        try {
          back.focus();
        } catch {
          /* ignore */
        }
      });
    }
  }, []);

  useEffect(() => {
    if (!editModal) return undefined;
    function onKeyDown(e) {
      if (e.key !== 'Escape') return;
      if (editBusy) return;
      e.preventDefault();
      closeEditModal();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [editModal, editBusy, closeEditModal]);

  async function createTenant(e) {
    e.preventDefault();
    setError('');
    setCopyOk(false);
    setNewShopUrl('');
    setBusy(true);
    setTokenPlain('');
    setTokenNote('');
    setErpClientIdOnce('');
    setErpClientSecretOnce('');

    const effectiveSlug = slug.trim() ? toSlug(slug.trim()) : toSlug(nome);
    if (isReservedStoreSlug(effectiveSlug)) {
      setError(
        'Este nome de página está reservado ou inválido para a URL pública. Escolha outro (apenas letras, números e hífen).'
      );
      setBusy(false);
      return;
    }

    try {
      const body = slug.trim() ? { nome, slug: slug.trim() } : { nome };
      const res = await api('/tenants', { method: 'POST', body: JSON.stringify(body) });
      setNome('');
      setSlug('');
      if (res.token_api_plain) setTokenPlain(res.token_api_plain);
      if (res.token_erp_note) setTokenNote(res.token_erp_note);
      if (res.erp_client_id) setErpClientIdOnce(String(res.erp_client_id));
      if (res.erp_client_secret) setErpClientSecretOnce(String(res.erp_client_secret));
      const path = res?.tenant?.slug;
      if (path && origin) setNewShopUrl(`${origin}/${path}`);
      await reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function regenerate(tid) {
    setBusy(true);
    try {
      const res = await api(`/tenants/${tid}/regenerate-token`, { method: 'POST' });
      if (res.token_api_plain) {
        setTokenPlain(res.token_api_plain);
        setTokenNote('');
      }
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  /** @param {'dados' | 'vitrine_inicial' | 'catalogo_luxe'} [initialTab] */
  function openEditModal(t, initialTab = 'dados') {
    editModalFocusReturnRef.current =
      typeof document !== 'undefined' && document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    setEditError('');
    setLogoFile(null);
    setEditNome(t.nome ?? '');
    setEditSlug(t.slug ?? '');
    setLogoUrlInput((t.logo_url && String(t.logo_url)) || '');
    setEditErpClientId((t.erp_client_id && String(t.erp_client_id)) || '');
    setEditErpClientSecret('');
    setEditStoreAccent(parseStoreAccent(t.theme_json));
    setEditHidePricesRequireLogin(tenantFlagFromApi(t.hide_prices_require_login));
    setEditBlockCustomerPublicRegistration(tenantFlagFromApi(t.block_customer_public_registration));
    setEditRequireCustomerEmail(tenantFlagFromApi(t.require_customer_email ?? 1));
    setEditRequireCustomerWhatsapp(tenantFlagFromApi(t.require_customer_whatsapp));
    setEditRequireAdultConfirmation(tenantFlagFromApi(t.require_adult_confirmation));
    setEditCatalogOnlyMode(tenantFlagFromApi(t.catalog_only_mode));
    setEditCatalogNoPhotoMode(tenantFlagFromApi(t.catalog_no_photo_mode));
    setEditBlockSaleZeroStock(tenantFlagFromApi(t.block_sale_zero_stock));
    setEditPickupOnlyMode(tenantFlagFromApi(t.pickup_only_mode));
    setEditPaymentOnline(tenantFlagFromApi(t.payment_online_enabled));
    setEditMpAccessToken('');
    setEditContactAddress((t.contact_address && String(t.contact_address)) || '');
    setEditContactWhatsapp((t.contact_whatsapp && String(t.contact_whatsapp)) || '');
    setEditContactMaps((t.contact_google_maps_url && String(t.contact_google_maps_url)) || '');
    setEditContactFacebook((t.contact_facebook_url && String(t.contact_facebook_url)) || '');
    setEditContactInstagram((t.contact_instagram_url && String(t.contact_instagram_url)) || '');
    setEditContactYoutube((t.contact_youtube_url && String(t.contact_youtube_url)) || '');
    setEditContactHours((t.contact_opening_hours && String(t.contact_opening_hours)) || '');
    setEditTimezone((t.timezone && String(t.timezone).trim()) || '');
    setEditCheckoutPaymentLines(
      Array.isArray(t.checkout_payment_methods) ? t.checkout_payment_methods.join('\n') : '',
    );
    setEditCheckoutOnlineLabel(
      t.checkout_online_payment_label != null ? String(t.checkout_online_payment_label) : '',
    );
    setEditTpvId(t.tpv_id != null && String(t.tpv_id).trim() !== '' ? String(t.tpv_id).trim() : '');
    setNewCouponCode('');
    setNewCouponKind('single');
    setNewCouponValidUntil('');
    setNewCouponMaxUses('');
    setDiscountCoupons([]);
    setEditWaShToken('');
    setClearWaShToken(false);
    setEditTenantModalTab(
      initialTab === 'vitrine_inicial'
        ? 'vitrine_inicial'
        : initialTab === 'catalogo_luxe'
          ? 'catalogo_luxe'
          : initialTab === 'formato_lista'
            ? 'formato_lista'
            : 'dados',
    );
    const shl = parseStoreHomeLayout(t.theme_json);
    const heroCfg = parseStoreHero(t.theme_json);
    const luxe = parseStoreCatalogLuxe(t.theme_json);
    const listFmt = parseStoreCatalogList(t.theme_json);
    setEditStoreHomeEnabled(shl.enabled);
    setEditStoreHomeLayoutJson(JSON.stringify(storeHomeLayoutBodyForEditor(t.theme_json), null, 2));
    setEditStoreHeroEnabled(heroCfg.enabled);
    setEditStoreHeroAutoplay(heroCfg.autoplaySeconds);
    setEditStoreHeroSlides(heroCfg.slides);
    setEditCatalogLuxeEnabled(luxe.enabled);
    setEditCatalogLuxeJson(JSON.stringify(storeCatalogLuxeBodyForEditor(t.theme_json), null, 2));
    setEditCatalogListEnabled(listFmt.enabled);
    setEditCatalogListJson(JSON.stringify(storeCatalogListBodyForEditor(t.theme_json), null, 2));
    setEditStoreProductDisplayJson(JSON.stringify(storeProductDisplayBodyForEditor(t.theme_json), null, 2));
    const analytics = storeAnalyticsBodyForEditor(t.theme_json);
    setEditGa4MeasurementId(analytics.ga4MeasurementId);
    setEditMetaPixelId(analytics.metaPixelId);
    const docOrder = storeDocumentOrderBodyForEditor(t.theme_json);
    setEditDocumentOrderEnabled(docOrder.enabled);
    setEditOpenAiKey('');
    setEditAnthropicApiKey('');
    setEditOpenAiConfigured(docOrder.openaiConfigured);
    setEditAnthropicConfigured(docOrder.anthropicConfigured);
    tenantEditBooleanBaselineRef.current = {
      hide_prices_require_login: tenantFlagFromApi(t.hide_prices_require_login),
      block_customer_public_registration: tenantFlagFromApi(t.block_customer_public_registration),
      require_customer_email: tenantFlagFromApi(t.require_customer_email ?? 1),
      require_customer_whatsapp: tenantFlagFromApi(t.require_customer_whatsapp),
      require_adult_confirmation: tenantFlagFromApi(t.require_adult_confirmation),
      catalog_only_mode: tenantFlagFromApi(t.catalog_only_mode),
      catalog_no_photo_mode: tenantFlagFromApi(t.catalog_no_photo_mode),
      block_sale_zero_stock: tenantFlagFromApi(t.block_sale_zero_stock),
      pickup_only_mode: tenantFlagFromApi(t.pickup_only_mode),
      payment_online_enabled: tenantFlagFromApi(t.payment_online_enabled),
    };
    setEditModal(t);
  }

  useEffect(() => {
    const id = editModal?.id;
    if (!id) {
      setWaStatus(null);
      return undefined;
    }
    let active = true;
    setWaStatus(null);
    (async () => {
      try {
        const res = await api(`/tenants/${id}/wa-status`);
        if (active) setWaStatus(res);
      } catch {
        if (active) setWaStatus(null);
      }
    })();
    return () => {
      active = false;
    };
  }, [editModal?.id]);

  async function submitNewCoupon() {
    if (!editModal) return;
    const code = newCouponCode.trim();
    const pct = Number(String(newCouponPct).replace(',', '.'));
    if (!code) {
      setEditError('Indique o código do cupom.');
      return;
    }
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      setEditError('Percentual inválido — use um valor entre 0 e 100.');
      return;
    }
    const kind = newCouponKind === 'multi' ? 'multi' : 'single';
    /** @type{Record<string, unknown>} */
    const body = { code, percent_discount: pct, kind };
    if (kind === 'multi') {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(newCouponValidUntil.trim())) {
        setEditError('Campanha: indique o último dia válido do cupom (data AAAA-MM-DD).');
        return;
      }
      body.valid_until = newCouponValidUntil.trim();
      const mu = String(newCouponMaxUses).trim();
      if (mu !== '') {
        const n = Number(mu.replace(/\s/g, ''));
        if (!Number.isFinite(n) || n < 1 || n > 100000000) {
          setEditError('Limite de usos: número inteiro entre 1 e 100 milhões, ou vazio para ilimitado até à data.');
          return;
        }
        body.max_uses = Math.floor(n);
      }
    }
    setEditError('');
    setCouponSaveBusy(true);
    try {
      await api(`/tenants/${editModal.id}/discount-coupons`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setNewCouponCode('');
      setNewCouponKind('single');
      setNewCouponValidUntil('');
      setNewCouponMaxUses('');
      await reloadDiscountCoupons(editModal.id);
    } catch (err) {
      setEditError(err.message || 'Erro ao criar cupom.');
    } finally {
      setCouponSaveBusy(false);
    }
  }

  async function deleteDiscountCoupon(couponId) {
    if (!editModal) return;
    setEditError('');
    setCouponSaveBusy(true);
    try {
      await api(`/tenants/${editModal.id}/discount-coupons/${couponId}`, { method: 'DELETE' });
      await reloadDiscountCoupons(editModal.id);
    } catch (err) {
      setEditError(err.message || 'Não foi possível remover o cupom.');
    } finally {
      setCouponSaveBusy(false);
    }
  }

  async function toggleDiscountCouponActive(couponId, activate) {
    if (!editModal) return;
    setEditError('');
    setCouponSaveBusy(true);
    try {
      await api(`/tenants/${editModal.id}/discount-coupons/${couponId}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: activate }),
      });
      await reloadDiscountCoupons(editModal.id);
    } catch (err) {
      setEditError(err.message || 'Não foi possível alterar o estado do cupom.');
    } finally {
      setCouponSaveBusy(false);
    }
  }

  function couponIsActive(c) {
    return tenantFlagFromApi(c?.is_active ?? 1);
  }

  async function reloadDiscountCoupons(tenantId) {
    try {
      const d = await api(`/tenants/${tenantId}/discount-coupons`);
      setDiscountCoupons(Array.isArray(d?.coupons) ? d.coupons : []);
    } catch (err) {
      setDiscountCoupons([]);
      setEditError(err?.message || 'Não foi possível carregar os cupons desta loja.');
    }
  }

  async function saveTenantEdit(e) {
    e.preventDefault();
    if (!editModal) return;
    const nomeTrim = editNome.trim();
    if (nomeTrim.length < 2) {
      setEditError('O nome da empresa deve ter pelo menos 2 caracteres.');
      return;
    }
    const slugRaw = editSlug.trim();
    if (!slugRaw) {
      setEditError('Indique o endereço da loja (slug).');
      return;
    }
    const slugNorm = toSlug(slugRaw);
    if (!slugNorm) {
      setEditError('Slug inválido: use letras, números ou hífen.');
      return;
    }
    if (slugNorm !== editModal.slug && isReservedStoreSlug(slugNorm)) {
      setEditError(
        'Este endereço da loja está reservado ou é inválido. Escolha outro (apenas letras, números e hífen).'
      );
      return;
    }

    setEditError('');
    setEditBusy(true);
    try {
      if (logoFile) {
        const fd = new FormData();
        fd.append('imagem', logoFile);
        await api(`/tenants/${editModal.id}/logo`, { method: 'POST', body: fd });
      }

      const patch = {};
      if (nomeTrim !== (editModal.nome ?? '')) {
        patch.nome = nomeTrim;
      }
      if (slugNorm !== (editModal.slug ?? '')) {
        patch.slug = slugRaw;
      }
      if (!logoFile) {
        const trimmed = logoUrlInput.trim();
        const origUrl = (editModal.logo_url && String(editModal.logo_url)) || '';
        // Campo vazio = "não mexer na logo" (evita apagar quando o input não mostra URL relativa ou não foi tocado).
        // Só «Remover logo» envia logo_url: null.
        if (trimmed !== origUrl && trimmed !== '') {
          patch.logo_url = trimmed;
        }
      }

      const prevTheme = normalizeThemeJson(editModal.theme_json);
      let layoutBody = {};
      try {
        layoutBody = editStoreHomeLayoutJson.trim() === '' ? {} : JSON.parse(editStoreHomeLayoutJson);
      } catch {
        setEditError('Vitrine inicial: JSON inválido. Confira vírgulas, aspas e chaves.');
        setEditBusy(false);
        return;
      }
      if (!layoutBody || typeof layoutBody !== 'object' || Array.isArray(layoutBody)) {
        setEditError('Vitrine inicial: o JSON tem de ser um objecto (por exemplo {"hero":[],"sections":[]}).');
        setEditBusy(false);
        return;
      }
      let luxeBody = {};
      try {
        luxeBody = editCatalogLuxeJson.trim() === '' ? {} : JSON.parse(editCatalogLuxeJson);
      } catch {
        setEditError('Catálogo luxo: JSON inválido. Confira vírgulas, aspas e chaves.');
        setEditBusy(false);
        return;
      }
      if (!luxeBody || typeof luxeBody !== 'object' || Array.isArray(luxeBody)) {
        setEditError('Catálogo luxo: o JSON tem de ser um objecto.');
        setEditBusy(false);
        return;
      }
      let listBody = {};
      try {
        listBody = editCatalogListJson.trim() === '' ? {} : JSON.parse(editCatalogListJson);
      } catch {
        setEditError('Formato lista: JSON inválido. Confira vírgulas, aspas e chaves.');
        setEditBusy(false);
        return;
      }
      if (!listBody || typeof listBody !== 'object' || Array.isArray(listBody)) {
        setEditError('Formato lista: o JSON tem de ser um objecto.');
        setEditBusy(false);
        return;
      }
      let displayBody = {};
      try {
        displayBody = editStoreProductDisplayJson.trim() === '' ? {} : JSON.parse(editStoreProductDisplayJson);
      } catch {
        setEditError('Imagens na loja: JSON inválido.');
        setEditBusy(false);
        return;
      }
      if (!displayBody || typeof displayBody !== 'object' || Array.isArray(displayBody)) {
        setEditError('Imagens na loja: o JSON tem de ser um objecto.');
        setEditBusy(false);
        return;
      }
      const luxeOn = Boolean(editCatalogLuxeEnabled);
      const listOn = luxeOn ? false : Boolean(editCatalogListEnabled);
      const vitrineOn = luxeOn || listOn ? false : Boolean(editStoreHomeEnabled);
      const ga4Norm = editGa4MeasurementId.trim().toUpperCase();
      const pixelNorm = editMetaPixelId.trim();
      if (ga4Norm && !/^G-[A-Z0-9]+$/.test(ga4Norm)) {
        setEditError('Google Analytics: use um ID de medição válido (ex.: G-XXXXXXXXXX).');
        setEditBusy(false);
        return;
      }
      if (pixelNorm && !/^\d+$/.test(pixelNorm)) {
        setEditError('Meta Pixel: use apenas dígitos numéricos.');
        setEditBusy(false);
        return;
      }
      const nextTheme = {
        ...prevTheme,
        storeAccent: editStoreAccent,
        storeHomeLayout: { ...layoutBody, enabled: vitrineOn },
        storeHero: storeHeroForSave({
          enabled: Boolean(editStoreHeroEnabled),
          autoplaySeconds: editStoreHeroAutoplay,
          slides: editStoreHeroSlides,
        }),
        storeCatalogLuxe: { ...luxeBody, enabled: luxeOn },
        storeCatalogList: { ...listBody, enabled: listOn },
        storeAnalytics: {
          ga4MeasurementId: ga4Norm,
          metaPixelId: pixelNorm,
        },
        storeDocumentOrder: storeDocumentOrderPatchForSave(prevTheme, {
          enabled: editDocumentOrderEnabled,
          openaiApiKey: editOpenAiKey,
          anthropicApiKey: editAnthropicApiKey,
        }),
        storeProductDisplay: {
          sectionPhotoFallback: Boolean(displayBody.sectionPhotoFallback),
        },
      };
      if (JSON.stringify(prevTheme) !== JSON.stringify(nextTheme)) {
        patch.theme_json = nextTheme;
      }
      const boolBase = tenantEditBooleanBaselineRef.current;
      const prevHide = boolBase
        ? boolBase.hide_prices_require_login
        : tenantFlagFromApi(editModal.hide_prices_require_login);
      if (editHidePricesRequireLogin !== prevHide) {
        patch.hide_prices_require_login = editHidePricesRequireLogin;
      }
      const prevBlock = boolBase
        ? boolBase.block_customer_public_registration
        : tenantFlagFromApi(editModal.block_customer_public_registration);
      if (editBlockCustomerPublicRegistration !== prevBlock) {
        patch.block_customer_public_registration = editBlockCustomerPublicRegistration;
      }
      const prevReqEmail = boolBase
        ? boolBase.require_customer_email
        : tenantFlagFromApi(editModal.require_customer_email ?? 1);
      if (editRequireCustomerEmail !== prevReqEmail) {
        patch.require_customer_email = editRequireCustomerEmail;
      }
      const prevReqWhatsapp = boolBase
        ? boolBase.require_customer_whatsapp
        : tenantFlagFromApi(editModal.require_customer_whatsapp);
      if (editRequireCustomerWhatsapp !== prevReqWhatsapp) {
        patch.require_customer_whatsapp = editRequireCustomerWhatsapp;
      }
      if (!editRequireCustomerEmail && !editRequireCustomerWhatsapp) {
        setEditError('Active pelo menos uma opção: exigir e-mail ou exigir WhatsApp.');
        setEditBusy(false);
        return;
      }
      const prevAdult = boolBase
        ? boolBase.require_adult_confirmation
        : tenantFlagFromApi(editModal.require_adult_confirmation);
      if (editRequireAdultConfirmation !== prevAdult) {
        patch.require_adult_confirmation = editRequireAdultConfirmation;
      }
      const prevCatOnly = boolBase
        ? boolBase.catalog_only_mode
        : tenantFlagFromApi(editModal.catalog_only_mode);
      if (editCatalogOnlyMode !== prevCatOnly) {
        patch.catalog_only_mode = editCatalogOnlyMode;
      }
      const prevNoPhoto = boolBase
        ? boolBase.catalog_no_photo_mode
        : tenantFlagFromApi(editModal.catalog_no_photo_mode);
      if (editCatalogNoPhotoMode !== prevNoPhoto) {
        patch.catalog_no_photo_mode = editCatalogNoPhotoMode;
      }
      const prevZero = boolBase
        ? boolBase.block_sale_zero_stock
        : tenantFlagFromApi(editModal.block_sale_zero_stock);
      if (editBlockSaleZeroStock !== prevZero) {
        patch.block_sale_zero_stock = editBlockSaleZeroStock;
      }
      const prevPickupOnly = boolBase
        ? boolBase.pickup_only_mode
        : tenantFlagFromApi(editModal.pickup_only_mode);
      if (editPickupOnlyMode !== prevPickupOnly) {
        patch.pickup_only_mode = editPickupOnlyMode;
      }
      if (editContactAddress.trim() !== String(editModal.contact_address || '')) {
        patch.contact_address = editContactAddress.trim() || null;
      }
      if (editContactWhatsapp.trim() !== String(editModal.contact_whatsapp || '')) {
        patch.contact_whatsapp = editContactWhatsapp.trim() || null;
      }
      if (editContactMaps.trim() !== String(editModal.contact_google_maps_url || '')) {
        patch.contact_google_maps_url = editContactMaps.trim() || null;
      }
      if (editContactFacebook.trim() !== String(editModal.contact_facebook_url || '')) {
        patch.contact_facebook_url = editContactFacebook.trim() || null;
      }
      if (editContactInstagram.trim() !== String(editModal.contact_instagram_url || '')) {
        patch.contact_instagram_url = editContactInstagram.trim() || null;
      }
      if (editContactYoutube.trim() !== String(editModal.contact_youtube_url || '')) {
        patch.contact_youtube_url = editContactYoutube.trim() || null;
      }
      if (editContactHours.trim() !== String(editModal.contact_opening_hours || '')) {
        patch.contact_opening_hours = editContactHours.trim() || null;
      }
      const prevTz = String(editModal.timezone || '').trim();
      if (editTimezone.trim() !== prevTz) {
        patch.timezone = editTimezone.trim() === '' ? null : editTimezone.trim();
      }
      const prevPm = Array.isArray(editModal.checkout_payment_methods)
        ? editModal.checkout_payment_methods.join('\n')
        : '';
      if (editCheckoutPaymentLines.trim() !== prevPm.trim()) {
        const lines = editCheckoutPaymentLines
          .split(/[\r\n]+/)
          .map((s) => s.trim())
          .filter(Boolean);
        patch.checkout_payment_methods = lines;
      }
      const prevOnlineLabel =
        editModal.checkout_online_payment_label != null
          ? String(editModal.checkout_online_payment_label)
          : '';
      if (editCheckoutOnlineLabel.trim() !== prevOnlineLabel.trim()) {
        const clean = editCheckoutOnlineLabel.trim();
        patch.checkout_online_payment_label = clean === '' ? null : clean;
      }
      const prevTpv = editModal.tpv_id != null ? String(editModal.tpv_id).trim() : '';
      if (editTpvId.trim() !== prevTpv) {
        const tpvDigits = editTpvId.replace(/\D/g, '').slice(0, 3);
        patch.tpv_id = tpvDigits === '' ? null : tpvDigits;
      }

      const erpPatch = {};
      if (canSaveErp) {
        const prevId = (editModal.erp_client_id && String(editModal.erp_client_id)) || '';
        if (editErpClientId.trim() !== prevId) {
          erpPatch.erp_client_id = editErpClientId.trim() === '' ? null : editErpClientId.trim();
        }
        if (editErpClientSecret.trim() !== '') {
          erpPatch.erp_client_secret = editErpClientSecret.trim();
        }
      }

      const mpPatch = {};
      const prevPayOnline = boolBase
        ? boolBase.payment_online_enabled
        : tenantFlagFromApi(editModal.payment_online_enabled);
      if (editPaymentOnline !== prevPayOnline) {
        mpPatch.payment_online_enabled = editPaymentOnline;
      }
      if (editMpAccessToken.trim() !== '') {
        mpPatch.gateway_mercado_pago = editMpAccessToken.trim();
      }

      const waShPatch = {};
      if (clearWaShToken) {
        waShPatch.clear_wa_sh_api_token = true;
      } else if (editWaShToken.trim() !== '') {
        waShPatch.wa_sh_api_token = editWaShToken.trim();
      }

      const mergedSettings = { ...patch, ...erpPatch, ...mpPatch, ...waShPatch };
      const mergedKeys = Object.keys(mergedSettings).filter((k) => mergedSettings[k] !== undefined);
      if (mergedKeys.length === 0 && !logoFile) {
        setEditError(
          'Nenhum campo alterado para gravar. Se mudou um interruptor e vê esta mensagem, actualize a página (F5) e tente de novo.',
        );
        setEditBusy(false);
        return;
      }
      if (mergedKeys.length > 0) {
        await api(`/tenants/${editModal.id}/settings`, {
          method: 'PATCH',
          body: JSON.stringify(mergedSettings),
        });
      }

      closeEditModal();
      setLogoFile(null);
      await reload();
    } catch (err) {
      setEditError(err.message);
    } finally {
      setEditBusy(false);
    }
  }

  async function saveOwnerWhatsapp(ev) {
    ev.preventDefault();
    setOwnerWhatsappMsg('');
    setOwnerWhatsappBusy(true);
    try {
      const res = await api('/auth/me', {
        method: 'PATCH',
        body: JSON.stringify({ whatsapp: ownerWhatsapp.trim() }),
      });
      setOwnerWhatsapp(String(res?.user?.whatsapp || ownerWhatsapp.trim()));
      setOwnerWhatsappMsg('WhatsApp atualizado. Este número receberá cópia dos pedidos das suas lojas.');
    } catch (err) {
      setOwnerWhatsappMsg(err.message || 'Não foi possível atualizar o WhatsApp.');
    } finally {
      setOwnerWhatsappBusy(false);
    }
  }

  async function removeLogo() {
    if (!editModal) return;
    setEditError('');
    setEditBusy(true);
    try {
      await api(`/tenants/${editModal.id}/settings`, {
        method: 'PATCH',
        body: JSON.stringify({ logo_url: null }),
      });
      setLogoUrlInput('');
      await reload();
      setEditModal((prev) =>
        prev && prev.id === editModal.id ? { ...prev, logo_url: null } : prev
      );
    } catch (err) {
      setEditError(err.message);
    } finally {
      setEditBusy(false);
    }
  }

  async function copyPublicLink(text) {
    try {
      await navigator.clipboard.writeText(text);
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 2500);
    } catch {
      setError('Não foi possível copiar — selecione e copie manualmente.');
    }
  }

  async function copyToClipboardSnippet(text, kind) {
    try {
      await navigator.clipboard.writeText(text);
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 2000);
    } catch {
      setEditError(kind ? `Copiar (${kind}) falhou — use seleção manual.` : 'Copiar falhou.');
    }
  }

  const erpStoredSecretDisplay =
    editModal && editModal.erp_client_secret != null && String(editModal.erp_client_secret).trim() !== ''
      ? String(editModal.erp_client_secret).trim()
      : '';
  const erpUrlLooksLikeViteDev =
    typeof window !== 'undefined' &&
    (/^127\.0\.0\.1(:\d+)?$/i.test(window.location.host || '') ||
      /^localhost(:\d+)?$/i.test(window.location.host || ''));

  return (
    <div className="space-y-8 py-8">
      {user?.role !== 'super_admin' && tenants.length === 0 && (
        <div className="rounded-3xl border border-accent/40 bg-gradient-to-r from-accent/12 to-emerald-500/10 px-6 py-5 text-sm shadow-lg dark:border-emerald-400/30">
          <h3 className="font-display text-xl font-bold text-ink dark:text-mist">Passo 1 — crie sua primeira empresa</h3>
          <p className="mt-2 max-w-3xl text-ink/80 dark:text-mist/80">
            Você acabou de entrar: cadastre abaixo o <strong>nome da empresa</strong> e, se quiser, o{' '}
            <strong>nome da página pública</strong>. O link que seus clientes usarão para comprar fica no formato{' '}
            <code className="rounded bg-black/10 px-1.5 py-0.5 font-mono text-xs dark:bg-white/15">
              {(shopHost || 'seudominio.com') + '/nomedaloja'}
            </code>
            . O sistema já trata isso como sua &quot;subpasta&quot; lógica de loja neste domínio.
          </p>
        </div>
      )}

      {billingNotice ? (
        <div className="rounded-2xl border border-sky-500/35 bg-sky-500/10 px-4 py-3 text-sm font-medium text-sky-950 dark:text-sky-100">
          {billingNotice}
          <button
            type="button"
            className="mt-2 block text-xs font-semibold underline underline-offset-2"
            onClick={() => setBillingNotice('')}
          >
            Fechar aviso
          </button>
        </div>
      ) : null}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl space-y-2">
          <h2 className="font-display text-3xl font-semibold text-ink dark:text-mist">Empresas</h2>
          <p className="text-sm text-ink/60 dark:text-frost-muted">
            Use <strong className="font-semibold text-ink dark:text-mist">Usar esta empresa</strong> para definir onde
            valem Produtos e Pedidos. <strong className="font-semibold text-ink dark:text-mist">Configurações</strong>{' '}
            abre o painel completo; <strong className="font-semibold text-ink dark:text-mist">Parâmetros da vitrine</strong>{' '}
            leva directo a essa aba.
          </p>
        </div>
        {user?.role === 'super_admin' && (
          <div className="max-w-md space-y-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-xs font-semibold text-amber-900 dark:text-amber-100">
            <p>
              Conta <strong>admin global</strong>: crie e edite empresas no painel global. O formulário «Nova empresa»
              desta página fica oculto para si, para evitar fluxos duplicados.
            </p>
            <Link
              to="/admin"
              className="inline-flex w-full items-center justify-center rounded-lg border border-amber-800/35 bg-white/95 px-3 py-2.5 text-center text-xs font-bold text-amber-950 shadow-sm transition hover:bg-white dark:border-amber-300/35 dark:bg-ink dark:text-amber-50 dark:hover:bg-white/10 sm:w-auto"
            >
              Abrir painel global
            </Link>
          </div>
        )}
      </div>

      <form
        onSubmit={saveOwnerWhatsapp}
        className="rounded-2xl border border-emerald-500/35 bg-emerald-500/10 px-4 py-4 text-sm dark:border-emerald-400/35 dark:bg-emerald-500/10"
      >
        <h3 className="font-semibold text-emerald-900 dark:text-emerald-100">WhatsApp para receber cópia dos pedidos</h3>
        <p className="mt-1 text-xs text-emerald-900/80 dark:text-emerald-100/85">
          Use o número do dono da loja com DDI (ex.: 5511999998888).
        </p>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <input
            required
            value={ownerWhatsapp}
            onChange={(e) => setOwnerWhatsapp(e.target.value)}
            className="w-full rounded-xl border border-emerald-700/20 bg-white px-3 py-2 text-sm text-ink dark:border-emerald-300/20 dark:bg-[#101915] dark:text-mist"
            placeholder="WhatsApp com DDI"
          />
          <button
            type="submit"
            disabled={ownerWhatsappBusy}
            className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 dark:bg-emerald-600"
          >
            {ownerWhatsappBusy ? 'A guardar…' : 'Guardar'}
          </button>
        </div>
        {ownerWhatsappMsg ? <p className="mt-2 text-xs font-medium text-emerald-900 dark:text-emerald-100">{ownerWhatsappMsg}</p> : null}
      </form>

      <div className="grid gap-6 lg:grid-cols-[1.1fr,0.9fr]">
        <div className="space-y-3 rounded-3xl border border-ink/10 bg-white/80 p-6 shadow-xl backdrop-blur dark:border-white/10 dark:bg-white/5">
          <h3 className="font-semibold tracking-tight">Suas empresas</h3>
          <ul className="space-y-3">
            {tenants.map((t) => (
              <li
                key={t.id}
                className={`flex flex-col gap-3 rounded-2xl border px-4 py-3 lg:flex-row lg:items-start lg:justify-between ${
                  tenantId === t.id
                    ? 'border-accent bg-accent/5'
                    : 'border-transparent bg-black/[0.02] dark:bg-white/[0.04]'
                }`}
              >
                <div className="flex min-w-0 flex-1 flex-col gap-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-ink dark:text-mist">{t.nome}</div>
                      <div className="mt-0.5 font-mono text-xs text-ink/70 dark:text-mist/70">
                        {origin ? (
                          <a
                            className="underline decoration-dotted underline-offset-2 hover:text-accent"
                            href={`/${t.slug}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {`${shopHost}/${t.slug}`}
                          </a>
                        ) : (
                          `/${t.slug}`
                        )}{' '}
                        <span className="font-sans text-[10px] text-ink/50 dark:text-mist/70">(abrir loja)</span>
                      </div>
                    </div>
                    {tenantId === t.id ? (
                      <span
                        className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-full border border-emerald-600/35 bg-emerald-500/15 px-3 py-1.5 text-[11px] font-semibold text-emerald-900 dark:border-emerald-400/35 dark:bg-emerald-500/15 dark:text-emerald-100"
                        aria-current="true"
                      >
                        <svg className="h-3.5 w-3.5" viewBox="0 0 14 14" fill="none" aria-hidden>
                          <path
                            d="M11.5 3.5L5.25 9.75 2.5 7"
                            stroke="currentColor"
                            strokeWidth="1.75"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Empresa em uso
                      </span>
                    ) : (
                      <button
                        type="button"
                        disabled={busy || editBusy}
                        onClick={() => setTenantId(t.id)}
                        className="shrink-0 self-start rounded-full border border-ink/20 bg-white/90 px-3 py-1.5 text-[11px] font-semibold text-ink shadow-sm transition hover:border-accent hover:text-accent disabled:opacity-50 dark:border-white/15 dark:bg-white/10 dark:text-mist dark:hover:border-accent dark:hover:text-accent"
                      >
                        Usar esta empresa
                      </button>
                    )}
                  </div>
                  <p className="text-[10px] leading-snug text-ink/55 dark:text-mist/72">
                    Os menus <span className="font-semibold">Produtos</span>, <span className="font-semibold">Pedidos</span>{' '}
                    e similares usam a empresa marcada como «em uso».
                  </p>
                  <div className="flex w-full flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex shrink-0 self-start rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase ${
                        t.status === 'blocked'
                          ? 'bg-accent2/15 text-accent2'
                          : 'bg-emerald-500/10 text-emerald-800 dark:text-emerald-300'
                      }`}
                    >
                      {t.status}
                    </span>
                  </div>
                  <div className="hidden w-full flex-wrap items-center gap-2 sm:flex">
                    <button
                      type="button"
                      disabled={busy || editBusy}
                      onClick={() =>
                        setReportTenant({ id: t.id, nome: t.nome, slug: t.slug })
                      }
                      className="rounded-full border border-sky-600/35 bg-sky-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-sky-900 dark:border-sky-400/35 dark:bg-sky-500/10 dark:text-sky-100"
                    >
                      Relatório
                    </button>
                    <button
                      type="button"
                      disabled={busy || editBusy}
                      onClick={() => openEditModal(t, 'dados')}
                      className="rounded-full border border-ink/20 bg-white/90 px-3 py-1 text-[11px] font-semibold text-ink shadow-sm hover:border-accent hover:text-accent disabled:opacity-50 dark:border-white/15 dark:bg-white/10 dark:text-mist dark:hover:border-accent dark:hover:text-accent"
                    >
                      Configurações
                    </button>
                    <button
                      type="button"
                      disabled={busy || editBusy}
                      onClick={() => openEditModal(t, 'vitrine_inicial')}
                      className="rounded-full border border-emerald-700/30 bg-emerald-500/10 px-3 py-1 text-[11px] font-semibold text-emerald-900 hover:border-emerald-600 disabled:opacity-50 dark:border-emerald-400/25 dark:bg-emerald-500/10 dark:text-emerald-100 dark:hover:border-emerald-400"
                      title="Abre o mesmo painel directamente na aba da vitrine"
                    >
                      Parâmetros da vitrine
                    </button>
                    <button
                      type="button"
                      disabled={busy || editBusy}
                      onClick={() => openEditModal(t, 'catalogo_luxe')}
                      className="rounded-full border border-violet-700/30 bg-violet-500/10 px-3 py-1 text-[11px] font-semibold text-violet-900 hover:border-violet-600 disabled:opacity-50 dark:border-violet-400/25 dark:bg-violet-500/10 dark:text-violet-100 dark:hover:border-violet-400"
                      title="Apresentação editorial exclusiva (alternativa à vitrine modular)"
                    >
                      Catálogo luxo
                    </button>
                    <button
                      type="button"
                      disabled={busy || editBusy}
                      onClick={() => openEditModal(t, 'formato_lista')}
                      className="rounded-full border border-sky-700/30 bg-sky-500/10 px-3 py-1 text-[11px] font-semibold text-sky-900 hover:border-sky-600 disabled:opacity-50 dark:border-sky-400/25 dark:bg-sky-500/10 dark:text-sky-100 dark:hover:border-sky-400"
                      title="Produtos em linhas com quantidade directa"
                    >
                      Formato lista
                    </button>
                  </div>
                  <div className="flex w-full flex-col gap-2 sm:hidden">
                    <button
                      type="button"
                      disabled={busy || editBusy}
                      onClick={() => openEditModal(t, 'dados')}
                      className="w-full justify-center rounded-full border border-ink/20 bg-white/90 px-3 py-2 text-[11px] font-semibold text-ink shadow-sm hover:border-accent hover:text-accent disabled:opacity-50 dark:border-white/15 dark:bg-white/10 dark:text-mist dark:hover:border-accent dark:hover:text-accent"
                    >
                      Configurações
                    </button>
                    <details className="relative w-full">
                      <summary className="flex w-full cursor-pointer list-none items-center justify-center gap-2 rounded-full border border-ink/20 bg-ink/[0.04] px-3 py-2 text-[11px] font-semibold text-ink dark:border-white/15 dark:bg-white/[0.06] dark:text-mist [&::-webkit-details-marker]:hidden">
                        <span aria-hidden>⋯</span>
                        Mais acções
                      </summary>
                      <div
                        role="group"
                        aria-label={`Mais acções: ${t.nome}`}
                        className="mt-2 flex flex-col gap-2 border-t border-ink/10 pt-3 dark:border-white/10"
                      >
                        <button
                          type="button"
                          disabled={busy || editBusy}
                          onClick={(e) => {
                            e.currentTarget.closest('details')?.removeAttribute('open');
                            setReportTenant({ id: t.id, nome: t.nome, slug: t.slug });
                          }}
                          className="w-full justify-center rounded-full border border-sky-600/35 bg-sky-500/10 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-sky-900 dark:border-sky-400/35 dark:bg-sky-500/10 dark:text-sky-100"
                        >
                          Relatório
                        </button>
                        <button
                          type="button"
                          disabled={busy || editBusy}
                          onClick={(e) => {
                            e.currentTarget.closest('details')?.removeAttribute('open');
                            openEditModal(t, 'vitrine_inicial');
                          }}
                          className="w-full justify-center rounded-full border border-emerald-700/30 bg-emerald-500/10 px-3 py-2 text-[11px] font-semibold text-emerald-900 dark:border-emerald-400/25 dark:bg-emerald-500/10 dark:text-emerald-100"
                        >
                          Parâmetros da vitrine
                        </button>
                        <button
                          type="button"
                          disabled={busy || editBusy}
                          onClick={(e) => {
                            e.currentTarget.closest('details')?.removeAttribute('open');
                            openEditModal(t, 'catalogo_luxe');
                          }}
                          className="w-full justify-center rounded-full border border-violet-700/30 bg-violet-500/10 px-3 py-2 text-[11px] font-semibold text-violet-900 dark:border-violet-400/25 dark:bg-violet-500/10 dark:text-violet-100"
                        >
                          Catálogo luxo
                        </button>
                        <button
                          type="button"
                          disabled={busy || editBusy}
                          onClick={(e) => {
                            e.currentTarget.closest('details')?.removeAttribute('open');
                            openEditModal(t, 'formato_lista');
                          }}
                          className="w-full justify-center rounded-full border border-sky-700/30 bg-sky-500/10 px-3 py-2 text-[11px] font-semibold text-sky-900 dark:border-sky-400/25 dark:bg-sky-500/10 dark:text-sky-100"
                        >
                          Formato lista
                        </button>
                        {(user?.has_erp_api || user?.role === 'super_admin') && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={(e) => {
                              e.currentTarget.closest('details')?.removeAttribute('open');
                              void regenerate(t.id);
                            }}
                            title="Gera um novo Bearer para a API OminIA. O token anterior deixa de funcionar. Não altera X-Auth-Id / Secret do sincronizador ERP."
                            className="w-full justify-center rounded-full border border-ink/10 px-3 py-2 text-xs font-semibold dark:border-white/10"
                          >
                            Regenerar token da API
                          </button>
                        )}
                      </div>
                    </details>
                  </div>
                  {Number(t.sub_pending_cnt) > 0 || Number(t.billing_overdue_blocked) === 1 ? (
                    <div className="rounded-xl border border-violet-500/25 bg-violet-500/[0.07] px-3 py-2 text-[11px] leading-relaxed text-ink/85 dark:text-mist/85">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-semibold text-violet-950 dark:text-violet-100">Plataforma (SaaS)</span>
                        {Number(t.billing_overdue_blocked) === 1 ? (
                          <span className="rounded-full bg-accent2/15 px-2 py-0.5 text-[10px] font-bold uppercase text-accent2">
                            loja suspensa até pagar
                          </span>
                        ) : null}
                      </div>
                      {Number(t.sub_pending_cnt) > 0 ? (
                        <p className="mt-1 text-ink/70 dark:text-mist/70">
                          Mensalidade em aberto:{' '}
                          <span className="font-semibold text-ink dark:text-mist">{formatBrlPretty(t.sub_pending_sum_brl)}</span>
                          {t.sub_next_due_date ? (
                            <>
                              {' '}
                              · venc.{' '}
                              <span className="font-mono">{String(t.sub_next_due_date).slice(0, 10)}</span>
                            </>
                          ) : null}
                        </p>
                      ) : (
                        <p className="mt-1 text-ink/60 dark:text-frost-muted">Sem valores pendentes no registo atual.</p>
                      )}
                      {Number(t.sub_pending_cnt) > 0 ? (
                        <button
                          type="button"
                          disabled={
                            Boolean(
                              paySubBusyId != null && Number(paySubBusyId) === Number(t.id),
                            ) || busy
                          }
                          onClick={() => payOpenMonthlyStore(t.id)}
                          className="mt-2 w-full rounded-xl border border-sky-500/55 bg-white/90 px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-sky-950 shadow-sm hover:bg-white dark:bg-white/10 dark:text-sky-100 dark:hover:bg-white/15 lg:w-auto"
                        >
                          {paySubBusyId != null && Number(paySubBusyId) === Number(t.id)
                            ? 'A preparar checkout…'
                            : 'Pagar mensalidade em aberto'}
                        </button>
                      ) : (
                        <p className="mt-2 text-[10px] text-ink/50 dark:text-frost-muted">
                          Quando existir nova cobrança, o botão de pagamento aparece aqui.
                        </p>
                      )}
                    </div>
                  ) : null}
                </div>
                <div className="hidden flex-shrink-0 flex-col gap-2 lg:flex lg:items-end lg:justify-start">
                  {(user?.has_erp_api || user?.role === 'super_admin') && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => regenerate(t.id)}
                      title="Gera um novo Bearer para a API OminIA. O token anterior deixa de funcionar. Não altera X-Auth-Id / Secret do sincronizador ERP (configure em «Configurações»)."
                      className="rounded-xl border border-ink/10 px-3 py-2 text-xs font-semibold dark:border-white/10"
                    >
                      Regenerar token da API
                    </button>
                  )}
                </div>
              </li>
            ))}
            {!tenants.length && (
              <li className="text-sm text-ink/55 dark:text-mist/72">Nenhuma empresa encontrada para este usuário.</li>
            )}
          </ul>
        </div>

        {user?.role !== 'super_admin' && (
          <form
            onSubmit={createTenant}
            className="space-y-3 rounded-3xl border border-ink/10 bg-white/85 p-6 shadow-xl backdrop-blur dark:border-white/10 dark:bg-[#081219]"
          >
            <h3 className="font-semibold tracking-tight">Nova empresa</h3>
            <label className="block text-sm font-semibold">
              Nome da empresa (exibido na loja)
              <input
                required
                className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-ink"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
              />
            </label>
            <label className="block text-sm font-semibold">
              Nome da página da loja (opcional)
              <input
                className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-ink"
                placeholder="ex.: minhapadaria → vira /minhapadaria"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
              />
            </label>
            <p className="text-[11px] leading-relaxed text-ink/60 dark:text-mist/72">
              Se deixar em branco, geramos o slug a partir do nome da empresa. Seu cliente final acessa apenas{' '}
              <strong>um endereço curto na raiz</strong> do seu domínio (ex.:{' '}
              <code className="font-mono">/lojateste</code>).
            </p>
            {newShopUrl && (
              <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4 text-sm dark:border-emerald-400/40 dark:bg-emerald-500/15">
                <div className="font-semibold text-emerald-950 dark:text-emerald-50">Loja pública pronta para compartilhar</div>
                <div className="mt-2 break-all font-mono text-xs text-ink/90 dark:text-mist/95">{newShopUrl}</div>
                <button
                  type="button"
                  onClick={() => copyPublicLink(newShopUrl)}
                  className="mt-3 rounded-xl bg-ink px-4 py-2 text-xs font-semibold text-mist dark:bg-mist dark:text-ink"
                >
                  {copyOk ? 'Copiado!' : 'Copiar link de pedidos'}
                </button>
              </div>
            )}
            {tokenPlain && (
              <div className="rounded-xl bg-accent/15 p-3 text-xs font-semibold text-accent dark:text-emerald-200">
                <p className="font-display text-sm text-ink dark:text-mist">Token Bearer da API (OminIA)</p>
                <p className="mt-1 text-[11px] font-normal leading-relaxed text-ink/85 dark:text-mist/85">
                  Usa-se em chamadas HTTP à API da plataforma (pedidos, integrações). Não é o{' '}
                  <span className="font-semibold">X-Auth-Secret</span> do sincronizador ERP — esse define-se em{' '}
                  <span className="font-semibold">Configurações</span> da empresa.
                </p>
                <p className="mt-2 break-all font-mono text-[11px]">
                  Guarde agora — não será repetido: <span className="select-all">{tokenPlain}</span>
                </p>
              </div>
            )}
            {(erpClientIdOnce || erpClientSecretOnce) && (
              <div className="rounded-xl border border-amber-500/45 bg-amber-500/10 p-3 text-xs font-semibold text-amber-950 dark:border-amber-400/40 dark:bg-amber-500/15 dark:text-amber-50">
                <p className="font-semibold">Integração ERP (Plano Ouro) — guarde agora</p>
                <p className="mt-1 text-[11px] font-normal leading-relaxed opacity-90">
                  O segredo não volta a aparecer aqui; depois pode alterá-lo em «Editar empresa» se precisar.
                </p>
                {erpClientIdOnce ? (
                  <p className="mt-2 break-all font-mono text-[11px]">
                    <span className="font-sans font-semibold">Client ID (X-Auth-Id): </span>
                    {erpClientIdOnce}
                  </p>
                ) : null}
                {erpClientSecretOnce ? (
                  <p className="mt-1.5 break-all font-mono text-[11px]">
                    <span className="font-sans font-semibold">Secret (X-Auth-Secret): </span>
                    {erpClientSecretOnce}
                  </p>
                ) : null}
              </div>
            )}
            {tokenNote && !tokenPlain && (
              <p className="rounded-xl border border-amber-500/35 bg-amber-500/10 p-3 text-xs font-semibold text-amber-950 dark:border-amber-400/35 dark:bg-amber-500/15 dark:text-amber-50">
                {tokenNote}
              </p>
            )}
            {error && <p className="text-sm font-semibold text-accent2">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-2xl bg-ink px-4 py-2 text-sm font-semibold text-mist dark:bg-mist dark:text-ink"
            >
              Criar empresa e liberar página pública
            </button>
          </form>
        )}
      </div>

      {reportTenant ? (
        <AdminTenantReportModal
          tenant={reportTenant}
          onClose={() => setReportTenant(null)}
          reportPathPrefix="tenants"
          variant="tenant"
        />
      ) : null}

      {editModal && (
        <div
          className="fixed inset-0 z-50 flex min-h-0 items-center justify-center bg-black/50 p-3 backdrop-blur-sm sm:p-5 md:p-8"
          role="presentation"
          onClick={() => {
            if (!editBusy) {
              closeEditModal();
            }
          }}
        >
          <div
            role="dialog"
            aria-labelledby="edit-tenant-title"
            aria-describedby="edit-tenant-subtitle tenant-edit-checklist-hint"
            aria-modal="true"
            className={`max-h-[min(92vh,calc(100dvh-1.25rem))] w-full max-w-[calc(100vw-1.5rem)] overflow-y-auto overscroll-contain rounded-3xl border border-ink/10 bg-white p-4 shadow-2xl dark:border-white/10 dark:bg-[#161f1c] sm:max-w-2xl sm:p-6 md:max-w-3xl lg:max-w-4xl lg:p-8 xl:max-w-5xl ${
              editTenantModalTab === 'vitrine_inicial' || editTenantModalTab === 'catalogo_luxe' || editTenantModalTab === 'formato_lista'
                ? '2xl:max-w-6xl'
                : '2xl:max-w-5xl'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="edit-tenant-title" className="font-display text-xl font-semibold text-ink dark:text-mist">
              Configurações da empresa
            </h3>
            <p id="edit-tenant-subtitle" className="mt-1 text-xs text-ink/55 dark:text-frost-muted">
              A loja pública fica em <span className="font-mono">{origin ? `${shopHost}/` : '/'}</span>
              <span className="font-mono">{toSlug(editSlug.trim() || editNome) || '…'}</span>. O slug não pode estar
              em uso por outra empresa — o servidor valida antes de gravar.
            </p>
            <form className="mt-4 space-y-3" onSubmit={saveTenantEdit}>
              <div className="space-y-3 border-b border-ink/10 pb-3 dark:border-white/10">
                <TenantModalSetupChecklist
                  editModal={editModal}
                  editNome={editNome}
                  editSlug={editSlug}
                  logoUrlInput={logoUrlInput}
                  logoFile={logoFile}
                  editContactAddress={editContactAddress}
                  editContactWhatsapp={editContactWhatsapp}
                  editContactMaps={editContactMaps}
                  editStoreHomeEnabled={editStoreHomeEnabled}
                  editCatalogLuxeEnabled={editCatalogLuxeEnabled}
                  editCatalogListEnabled={editCatalogListEnabled}
                  editPaymentOnline={editPaymentOnline}
                  editErpClientId={editErpClientId}
                  canSaveErp={canSaveErp}
                />
                <div
                  className="flex flex-wrap gap-1 rounded-xl border border-ink/10 bg-ink/[0.04] p-1 dark:border-white/10 dark:bg-white/[0.05]"
                  role="tablist"
                  aria-label="Secções do editor de empresa"
                >
                  <button
                    type="button"
                    id="tenant-tab-dados"
                    ref={editModalDadosTabRef}
                    role="tab"
                    aria-selected={editTenantModalTab === 'dados'}
                    aria-controls="tenant-panel-dados"
                    className={`min-w-[calc(50%-0.25rem)] flex-1 rounded-lg px-2 py-2 text-[11px] font-semibold transition sm:min-w-0 sm:px-3 sm:text-xs ${
                      editTenantModalTab === 'dados'
                        ? 'bg-white text-ink shadow-sm dark:bg-[#252f2a] dark:text-mist'
                        : 'text-ink/70 hover:text-ink dark:text-mist/70 dark:hover:text-mist'
                    }`}
                    onClick={() => setEditTenantModalTab('dados')}
                  >
                    Dados da loja
                  </button>
                  <button
                    type="button"
                    id="tenant-tab-vitrine"
                    ref={editModalVitrineTabRef}
                    role="tab"
                    aria-selected={editTenantModalTab === 'vitrine_inicial'}
                    aria-controls="tenant-panel-vitrine"
                    className={`min-w-[calc(50%-0.25rem)] flex-1 rounded-lg px-2 py-2 text-[11px] font-semibold transition sm:min-w-0 sm:px-3 sm:text-xs ${
                      editTenantModalTab === 'vitrine_inicial'
                        ? 'bg-white text-ink shadow-sm dark:bg-[#252f2a] dark:text-mist'
                        : 'text-ink/70 hover:text-ink dark:text-mist/70 dark:hover:text-mist'
                    }`}
                    onClick={() => setEditTenantModalTab('vitrine_inicial')}
                  >
                    Parâmetros da vitrine
                  </button>
                  <button
                    type="button"
                    id="tenant-tab-catalogo-luxe"
                    role="tab"
                    aria-selected={editTenantModalTab === 'catalogo_luxe'}
                    aria-controls="tenant-panel-catalogo-luxe"
                    className={`min-w-[calc(50%-0.25rem)] flex-1 rounded-lg px-2 py-2 text-[11px] font-semibold transition sm:min-w-0 sm:px-3 sm:text-xs ${
                      editTenantModalTab === 'catalogo_luxe'
                        ? 'bg-white text-ink shadow-sm dark:bg-[#252f2a] dark:text-mist'
                        : 'text-ink/70 hover:text-ink dark:text-mist/70 dark:hover:text-mist'
                    }`}
                    onClick={() => setEditTenantModalTab('catalogo_luxe')}
                  >
                    Catálogo luxo
                  </button>
                  <button
                    type="button"
                    id="tenant-tab-formato-lista"
                    role="tab"
                    aria-selected={editTenantModalTab === 'formato_lista'}
                    aria-controls="tenant-panel-formato-lista"
                    className={`min-w-[calc(50%-0.25rem)] flex-1 rounded-lg px-2 py-2 text-[11px] font-semibold transition sm:min-w-0 sm:px-3 sm:text-xs ${
                      editTenantModalTab === 'formato_lista'
                        ? 'bg-white text-ink shadow-sm dark:bg-[#252f2a] dark:text-mist'
                        : 'text-ink/70 hover:text-ink dark:text-mist/70 dark:hover:text-mist'
                    }`}
                    onClick={() => setEditTenantModalTab('formato_lista')}
                  >
                    Formato lista
                  </button>
                </div>
              </div>
              <p className="text-[10px] leading-relaxed text-ink/55 dark:text-mist/72">
                Alterações nas abas só ficam na loja após <span className="font-semibold text-ink/70 dark:text-mist/70">Gravar empresa</span> no fim do formulário.
              </p>

              {editTenantModalTab === 'catalogo_luxe' ? (
                <div
                  id="tenant-panel-catalogo-luxe"
                  role="tabpanel"
                  aria-labelledby="tenant-tab-catalogo-luxe"
                  className="space-y-3 border-t border-ink/10 pt-4 dark:border-white/10"
                >
                  <p className="text-xs leading-relaxed text-ink/70 dark:text-mist/70">
                    Apresentação editorial completa da loja (hero, história, grelha de produtos).{' '}
                    <strong className="font-semibold">Exclusivo</strong> em relação à vitrine modular: ao activar o
                    catálogo luxo, a vitrine modular fica desligada. Sacola, checkout, fotos e partilha mantêm-se iguais.
                  </p>
                  <label className="flex cursor-pointer items-center gap-3 text-sm font-semibold text-ink dark:text-mist">
                    <input
                      type="checkbox"
                      checked={editCatalogLuxeEnabled}
                      onChange={(e) => {
                        const v = e.target.checked;
                        setEditCatalogLuxeEnabled(v);
                        if (v) {
                          setEditStoreHomeEnabled(false);
                          setEditCatalogListEnabled(false);
                        }
                      }}
                      className="h-4 w-4 rounded border-ink/30 text-violet-600 focus:ring-violet-500 dark:border-white/25"
                    />
                    Activar catálogo luxo nesta loja
                  </label>
                  {editModal ? (
                    <StoreCatalogLuxeForm
                      tenantId={editModal.id}
                      json={editCatalogLuxeJson}
                      onJsonChange={setEditCatalogLuxeJson}
                      disabled={editBusy}
                    />
                  ) : null}
                  <label className="block text-xs font-semibold text-ink dark:text-mist">
                    JSON avançado (opcional)
                    <textarea
                      rows={10}
                      spellCheck={false}
                      className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-[11px] leading-snug dark:border-white/10 dark:bg-[#252f2a]"
                      value={editCatalogLuxeJson}
                      onChange={(e) => setEditCatalogLuxeJson(e.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    className="rounded-xl border border-ink/15 px-3 py-2 text-xs font-semibold text-ink/85 dark:border-white/20"
                    onClick={() => setEditCatalogLuxeJson(JSON.stringify(STORE_CATALOG_LUXE_EDITOR_EXAMPLE, null, 2))}
                  >
                    Inserir exemplo Belle vie
                  </button>
                </div>
              ) : editTenantModalTab === 'formato_lista' ? (
                <div
                  id="tenant-panel-formato-lista"
                  role="tabpanel"
                  aria-labelledby="tenant-tab-formato-lista"
                  className="space-y-3 border-t border-ink/10 pt-4 dark:border-white/10"
                >
                  <p className="text-xs leading-relaxed text-ink/70 dark:text-mist/70">
                    Catálogo em linhas: nome do produto, preço e controlos de quantidade (+/− ou digitar). Sem menu
                    lateral de categorias — use «Agrupar por secções» para títulos entre grupos.{' '}
                    <strong className="font-semibold">Exclusivo</strong> em relação à vitrine modular e ao catálogo luxo.
                  </p>
                  <label className="flex cursor-pointer items-center gap-3 text-sm font-semibold text-ink dark:text-mist">
                    <input
                      type="checkbox"
                      checked={editCatalogListEnabled}
                      onChange={(e) => {
                        const v = e.target.checked;
                        setEditCatalogListEnabled(v);
                        if (v) {
                          setEditStoreHomeEnabled(false);
                          setEditCatalogLuxeEnabled(false);
                        }
                      }}
                      disabled={editCatalogLuxeEnabled}
                      className="h-4 w-4 rounded border-ink/30 text-sky-600 focus:ring-sky-500 dark:border-white/25 disabled:opacity-40"
                    />
                    Activar formato lista nesta loja
                  </label>
                  {editCatalogLuxeEnabled ? (
                    <p className="text-xs text-violet-800 dark:text-violet-200">
                      Desactive o catálogo luxo para usar o formato lista.
                    </p>
                  ) : null}
                  <StoreCatalogListForm
                    json={editCatalogListJson}
                    onJsonChange={setEditCatalogListJson}
                    disabled={editBusy}
                  />
                  <label className="block text-xs font-semibold text-ink dark:text-mist">
                    JSON avançado (opcional)
                    <textarea
                      rows={6}
                      spellCheck={false}
                      className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-[11px] leading-snug dark:border-white/10 dark:bg-[#252f2a]"
                      value={editCatalogListJson}
                      onChange={(e) => setEditCatalogListJson(e.target.value)}
                    />
                  </label>
                </div>
              ) : editTenantModalTab === 'vitrine_inicial' ? (
                <div
                  id="tenant-panel-vitrine"
                  role="tabpanel"
                  aria-labelledby="tenant-tab-vitrine"
                  className="space-y-3 border-t border-ink/10 pt-4 dark:border-white/10"
                >
                  <p className="text-xs leading-relaxed text-ink/70 dark:text-mist/70">
                    Hero rotativo (estilo vitrine de loja grande) e, opcionalmente, layout modular no topo da loja
                    pública. Ambos só aparecem sem pesquisa nem categoria seleccionada. O hero funciona sozinho no
                    layout padrão; se a vitrine modular estiver ligada, partilha o mesmo carrossel.
                  </p>
                  {editModal ? (
                    <StoreHeroForm
                      tenantId={editModal.id}
                      enabled={editStoreHeroEnabled}
                      onEnabledChange={setEditStoreHeroEnabled}
                      autoplaySeconds={editStoreHeroAutoplay}
                      onAutoplayChange={setEditStoreHeroAutoplay}
                      slides={editStoreHeroSlides}
                      onSlidesChange={setEditStoreHeroSlides}
                      disabled={editBusy || editCatalogLuxeEnabled || editCatalogListEnabled}
                    />
                  ) : null}
                  <label className="flex cursor-pointer items-center gap-3 text-sm font-semibold text-ink dark:text-mist">
                    <input
                      type="checkbox"
                      checked={editStoreHomeEnabled}
                      onChange={(e) => {
                        const v = e.target.checked;
                        setEditStoreHomeEnabled(v);
                        if (v) {
                          setEditCatalogLuxeEnabled(false);
                          setEditCatalogListEnabled(false);
                        }
                      }}
                      disabled={editCatalogLuxeEnabled || editCatalogListEnabled}
                      className="h-4 w-4 rounded border-ink/30 text-accent focus:ring-accent dark:border-white/25 disabled:opacity-40"
                    />
                    Activar vitrine inicial (estilo vitrine modular)
                  </label>
                  {editCatalogLuxeEnabled || editCatalogListEnabled ? (
                    <p className="text-xs text-violet-800 dark:text-violet-200">
                      {editCatalogLuxeEnabled
                        ? 'Desligue o catálogo luxo para poder activar a vitrine modular.'
                        : 'Desligue o formato lista para poder activar a vitrine modular.'}
                    </p>
                  ) : null}
                  {editModal ? (
                    <StoreVitrineLayoutForm
                      tenantId={editModal.id}
                      json={editStoreHomeLayoutJson}
                      onJsonChange={setEditStoreHomeLayoutJson}
                      disabled={editBusy}
                    />
                  ) : null}
                  <label className="block text-xs font-semibold text-ink dark:text-mist">
                    JSON da vitrine (<span className="font-mono">hero</span> + <span className="font-mono">sections</span>)
                    <textarea
                      rows={16}
                      spellCheck={false}
                      className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-[11px] leading-snug text-ink dark:border-white/10 dark:bg-[#252f2a] dark:text-mist"
                      value={editStoreHomeLayoutJson}
                      onChange={(e) => setEditStoreHomeLayoutJson(e.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    className="rounded-xl border border-ink/15 px-3 py-2 text-xs font-semibold text-ink/85 dark:border-white/20 dark:text-mist/85"
                    onClick={() => setEditStoreHomeLayoutJson(STORE_HOME_LAYOUT_EDITOR_EXAMPLE)}
                  >
                    Inserir exemplo no editor
                  </button>
                </div>
              ) : (
                <div
                  id="tenant-panel-dados"
                  role="tabpanel"
                  aria-labelledby="tenant-tab-dados"
                  className="contents"
                >
              <label className="block text-sm font-semibold text-ink dark:text-mist">
                Nome da empresa (exibido na loja)
                <input
                  required
                  minLength={2}
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={editNome}
                  onChange={(e) => setEditNome(e.target.value)}
                />
              </label>
              <label className="block text-sm font-semibold text-ink dark:text-mist">
                Endereço da loja (slug)
                <input
                  required
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={editSlug}
                  onChange={(e) => setEditSlug(e.target.value)}
                  placeholder="ex.: minha-padaria"
                />
              </label>
              <p className="text-[11px] text-ink/58 dark:text-mist/72">
                Pré-visualização do caminho:{' '}
                <span className="font-mono text-ink/70 dark:text-mist/70">
                  /{toSlug(editSlug.trim() || editNome) || '…'}
                </span>
              </p>
              <div className="space-y-2 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.06] p-3">
                <p className="text-xs font-semibold text-emerald-900 dark:text-emerald-100">Dados adicionais da loja</p>
                <input
                  className="w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  placeholder="Endereço da loja"
                  value={editContactAddress}
                  onChange={(e) => setEditContactAddress(e.target.value)}
                />
                <input
                  className="w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  placeholder="Telefone / WhatsApp (com DDI)"
                  value={editContactWhatsapp}
                  onChange={(e) => setEditContactWhatsapp(e.target.value)}
                />
                <input
                  className="w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  placeholder="Link Google Maps"
                  value={editContactMaps}
                  onChange={(e) => setEditContactMaps(e.target.value)}
                />
                <input
                  className="w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  placeholder="Facebook"
                  value={editContactFacebook}
                  onChange={(e) => setEditContactFacebook(e.target.value)}
                />
                <input
                  className="w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  placeholder="Instagram"
                  value={editContactInstagram}
                  onChange={(e) => setEditContactInstagram(e.target.value)}
                />
                <input
                  className="w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  placeholder="YouTube"
                  value={editContactYoutube}
                  onChange={(e) => setEditContactYoutube(e.target.value)}
                />
                <input
                  className="w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  placeholder="Horário de funcionamento"
                  value={editContactHours}
                  onChange={(e) => setEditContactHours(e.target.value)}
                />
                <label className="block text-[11px] font-semibold text-ink/80 dark:text-mist/80">
                  Fuso horário (data em WhatsApp / e-mail)
                  <input
                    className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-xs dark:border-white/10 dark:bg-[#252f2a]"
                    list="ominia-store-timezone-list"
                    placeholder="ex.: America/Sao_Paulo"
                    value={editTimezone}
                    onChange={(e) => setEditTimezone(e.target.value)}
                    autoComplete="off"
                  />
                  <datalist id="ominia-store-timezone-list">
                    {STORE_TIMEZONE_IANA_SUGGESTIONS.map((z) => (
                      <option key={z} value={z} />
                    ))}
                  </datalist>
                  <span className="mt-0.5 block text-[10px] font-normal font-sans text-ink/55 dark:text-mist/72">
                    Vazio = usar o fuso global da API (<code className="rounded bg-black/10 px-1 dark:bg-white/15">OMINIA_DEFAULT_TIMEZONE</code> no .env).
                  </span>
                </label>
                <label className="block text-[11px] font-semibold text-ink/80 dark:text-mist/80">
                  Formas de pagamento no fechamento do pedido (uma por linha
                  {editPaymentOnline ? '' : ', só informativo'})
                  <textarea
                    rows={4}
                    className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-xs uppercase placeholder:normal-case dark:border-white/10 dark:bg-[#252f2a]"
                    placeholder="Ex.: Pix na entrega&#10;Dinheiro&#10;Cartao na maquininha"
                    value={editCheckoutPaymentLines}
                    onChange={(e) => setEditCheckoutPaymentLines(e.target.value)}
                  />
                  <span className="mt-0.5 block text-[10px] font-normal text-ink/55 dark:text-mist/72">
                    Ao gravar, o servidor guarda em <strong className="font-semibold">CAIXA ALTA</strong>, sem acentos nem
                    caracteres especiais.
                  </span>
                  {editPaymentOnline ? (
                    <span className="mt-2 block rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-[10px] font-normal leading-relaxed text-emerald-800 dark:border-emerald-400/30 dark:bg-emerald-500/15 dark:text-emerald-100">
                      <strong className="font-semibold">Pagamento online activo:</strong> a opção
                      {' '}
                      <code className="rounded bg-black/10 px-1 dark:bg-white/15">
                        {editCheckoutOnlineLabel.trim() !== ''
                          ? editCheckoutOnlineLabel.trim()
                          : 'PAGAR ONLINE (MERCADO PAGO)'}
                      </code>
                      {' '}
                      será adicionada automaticamente no combo do checkout, e só ela dispara o Mercado Pago. As formas
                      listadas acima continuam como informação (vão para o ERP/WhatsApp). Se você não cadastrar nenhuma
                      forma offline aqui, vamos incluir também
                      {' '}<code className="rounded bg-black/10 px-1 dark:bg-white/15">PAGAR NA ENTREGA / RETIRADA</code>{' '}
                      para o cliente ter alternativa.
                      <br />
                      <em className="font-normal">
                        Importante: a opção só aparece no checkout quando o Mercado Pago está com Access Token
                        configurado (por empresa ou global). Sem token, escondemos a opção para o cliente.
                      </em>
                    </span>
                  ) : null}
                </label>
              </div>

              <div className="border-t border-ink/10 pt-3 dark:border-white/10">
                <p className="text-xs font-semibold text-ink dark:text-mist">Cor de destaque da loja pública</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {STORE_ACCENT_KEYS.map((key) => (
                    <button
                      key={key}
                      type="button"
                      disabled={editBusy}
                      onClick={() => setEditStoreAccent(key)}
                      className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-left text-xs font-semibold transition ${
                        editStoreAccent === key
                          ? 'border-accent bg-accent/15 text-ink dark:text-mist'
                          : 'border-ink/15 bg-white/90 text-ink/85 hover:border-accent/40 dark:border-white/15 dark:bg-white/[0.05] dark:text-mist/85 dark:hover:border-accent/35'
                      }`}
                    >
                      <span
                        className="h-7 w-7 shrink-0 rounded-lg border border-black/10 shadow-inner dark:border-white/15"
                        style={{ backgroundColor: STORE_ACCENT_SWATCHES[key] }}
                        aria-hidden
                      />
                      {STORE_ACCENT_LABELS[key]}
                    </button>
                  ))}
                </div>
              </div>
              <div className="rounded-xl border border-ink/10 bg-black/[0.02] p-3 dark:border-white/10 dark:bg-white/[0.03]">
                <label className="flex cursor-pointer items-center gap-3 text-sm font-semibold text-ink dark:text-mist">
                  <input
                    type="checkbox"
                    checked={editHidePricesRequireLogin}
                    onChange={(e) => setEditHidePricesRequireLogin(e.target.checked)}
                    className="h-4 w-4 rounded border-ink/30 text-accent focus:ring-accent dark:border-white/25"
                  />
                  Ocultar preços e exigir cadastro/login do cliente para comprar
                </label>
              </div>
              <div className="rounded-xl border border-violet-500/30 bg-violet-500/[0.06] p-3 dark:border-violet-400/30 dark:bg-violet-500/[0.1]">
                <label className="flex cursor-pointer items-center gap-3 text-sm font-semibold text-ink dark:text-mist">
                  <input
                    type="checkbox"
                    checked={editBlockCustomerPublicRegistration}
                    onChange={(e) => setEditBlockCustomerPublicRegistration(e.target.checked)}
                    className="h-4 w-4 rounded border-ink/30 text-violet-600 focus:ring-violet-500 dark:border-white/25"
                  />
                  Bloquear cadastro público na loja (só login de quem já tem conta; clientes novos pelo painel ou ERP)
                </label>
              </div>
              <div className="rounded-xl border border-teal-500/30 bg-teal-500/[0.06] p-3 dark:border-teal-400/30 dark:bg-teal-500/[0.1]">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-teal-800 dark:text-teal-200">
                  Contacto do cliente (cadastro e checkout)
                </p>
                <div className="space-y-2">
                  <label className="flex cursor-pointer items-center gap-3 text-sm font-semibold text-ink dark:text-mist">
                    <input
                      type="checkbox"
                      checked={editRequireCustomerEmail}
                      onChange={(e) => setEditRequireCustomerEmail(e.target.checked)}
                      className="h-4 w-4 rounded border-ink/30 text-teal-600 focus:ring-teal-500 dark:border-white/25"
                    />
                    Exigir e-mail
                  </label>
                  <label className="flex cursor-pointer items-center gap-3 text-sm font-semibold text-ink dark:text-mist">
                    <input
                      type="checkbox"
                      checked={editRequireCustomerWhatsapp}
                      onChange={(e) => setEditRequireCustomerWhatsapp(e.target.checked)}
                      className="h-4 w-4 rounded border-ink/30 text-teal-600 focus:ring-teal-500 dark:border-white/25"
                    />
                    Exigir WhatsApp
                  </label>
                </div>
                <p className="mt-2 text-[11px] leading-snug text-teal-900/80 dark:text-teal-100/80">
                  Pelo menos uma opção deve ficar activa. Com as duas activas, o cliente informa{' '}
                  <strong>e-mail ou WhatsApp</strong> — não precisa dos dois.
                </p>
              </div>
              <div className="rounded-xl border border-rose-500/30 bg-rose-500/[0.06] p-3 dark:border-rose-400/30 dark:bg-rose-500/[0.1]">
                <label className="flex cursor-pointer items-center gap-3 text-sm font-semibold text-ink dark:text-mist">
                  <input
                    type="checkbox"
                    className="h-4 w-4 shrink-0 rounded border-ink/30 text-rose-600 focus:ring-rose-500 dark:border-white/25"
                    checked={editRequireAdultConfirmation}
                    onChange={(e) => setEditRequireAdultConfirmation(e.target.checked)}
                  />
                  Controle de maioridade (18+)
                </label>
              </div>
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-3 dark:border-amber-400/30 dark:bg-amber-500/[0.1]">
                <label className="flex cursor-pointer items-center gap-3 text-sm font-semibold text-ink dark:text-mist">
                  <input
                    type="checkbox"
                    checked={editCatalogOnlyMode}
                    onChange={(e) => setEditCatalogOnlyMode(e.target.checked)}
                    className="h-4 w-4 rounded border-ink/30 text-amber-600 focus:ring-amber-500 dark:border-white/25"
                  />
                  Modo catálogo digital (sem compras)
                </label>
              </div>
              <div className="rounded-xl border border-sky-500/30 bg-sky-500/[0.06] p-3 dark:border-sky-400/30 dark:bg-sky-500/[0.1]">
                <label className="flex cursor-pointer items-center gap-3 text-sm font-semibold text-ink dark:text-mist">
                  <input
                    type="checkbox"
                    checked={editCatalogNoPhotoMode}
                    onChange={(e) => setEditCatalogNoPhotoMode(e.target.checked)}
                    className="h-4 w-4 rounded border-ink/30 text-sky-600 focus:ring-sky-500 dark:border-white/25"
                  />
                  Catálogo sem foto nos cards
                </label>
              </div>
              <StoreProductDisplayForm
                json={editStoreProductDisplayJson}
                onJsonChange={setEditStoreProductDisplayJson}
                disabled={editBusy || couponSaveBusy}
              />
              <div className="rounded-xl border border-emerald-500/35 bg-emerald-500/[0.07] p-3 dark:border-emerald-400/35 dark:bg-emerald-500/[0.1]">
                <label className="flex cursor-pointer items-center gap-3 text-sm font-semibold text-ink dark:text-mist">
                  <input
                    type="checkbox"
                    checked={editBlockSaleZeroStock}
                    onChange={(e) => setEditBlockSaleZeroStock(e.target.checked)}
                    className="h-4 w-4 shrink-0 rounded border-ink/30 text-emerald-600 focus:ring-emerald-500 dark:border-white/25"
                  />
                  Bloquear venda estoque zero.
                </label>
              </div>

              <div className="rounded-xl border border-violet-500/35 bg-violet-500/[0.07] p-3 dark:border-violet-400/35 dark:bg-violet-500/[0.1]">
                <label className="flex cursor-pointer items-start gap-3 text-sm font-semibold text-ink dark:text-mist">
                  <input
                    type="checkbox"
                    checked={editPickupOnlyMode}
                    onChange={(e) => setEditPickupOnlyMode(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-ink/30 text-violet-600 focus:ring-violet-500 dark:border-white/25"
                  />
                  <span>
                    Só retirada na loja (sem entregas). No checkout o cliente não vê moradas nem endereço manual.
                  </span>
                </label>
              </div>

              <div className="space-y-2 rounded-xl border border-sky-500/25 bg-sky-500/[0.06] p-4 dark:border-sky-400/20">
                <p className="text-xs font-semibold text-ink dark:text-mist">WhatsApp (Software House)</p>
                {waStatus && !waStatus.ready ? (
                  <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] leading-relaxed text-amber-700 dark:text-amber-300">
                    <span className="font-semibold">⚠️ WhatsApp não está pronto para envio.</span>{' '}
                    {!waStatus.token_present ? 'Falta o token do gateway desta loja. ' : ''}
                    {!waStatus.endpoint_present ? 'Falta o endpoint do gateway (Software House). ' : ''}
                    Os pedidos continuam a ser criados, mas as mensagens de confirmação podem não ser
                    entregues. Configure o token do cliente e/ou a Software House.
                  </div>
                ) : null}
                {waStatus && waStatus.ready ? (
                  <p className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                    ✓ WhatsApp pronto para envio (origem: {waStatus.source}).
                  </p>
                ) : null}
                {!editModal.software_house_nome ? (
                  <p className="text-[11px] leading-relaxed text-ink/55 dark:text-frost-muted">
                    Esta loja usa o envio WhatsApp global da plataforma. Se lhe for atribuída uma Software House, o nome
                    aparecerá aqui para poder colar o token do cliente.
                  </p>
                ) : null}
                {editModal.software_house_nome ? (
                  <>
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-ink/65 dark:text-frost-muted">
                      Token API do cliente
                      <input
                        type="password"
                        autoComplete="new-password"
                        disabled={editBusy || couponSaveBusy}
                        placeholder={
                          Number(editModal.wa_sh_api_token_configured) === 1
                            ? '••• guardado — novo valor para substituir'
                            : 'cole o token fornecido pela Software House'
                        }
                        className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-sm dark:border-white/10 dark:bg-[#252f2a]"
                        value={editWaShToken}
                        onChange={(e) => {
                          setEditWaShToken(e.target.value);
                          setClearWaShToken(false);
                        }}
                      />
                    </label>
                    <label className="flex items-center gap-2 text-xs font-semibold text-ink dark:text-mist">
                      <input
                        type="checkbox"
                        checked={clearWaShToken}
                        onChange={(e) => {
                          setClearWaShToken(e.target.checked);
                          if (e.target.checked) setEditWaShToken('');
                        }}
                        disabled={editBusy || couponSaveBusy}
                      />
                      Remover token guardado
                    </label>
                  </>
                ) : null}
              </div>

              <StoreAnalyticsForm
                ga4Id={editGa4MeasurementId}
                pixelId={editMetaPixelId}
                onGa4Change={setEditGa4MeasurementId}
                onPixelChange={setEditMetaPixelId}
                disabled={editBusy || couponSaveBusy}
              />

              <StoreDocumentOrderForm
                enabled={editDocumentOrderEnabled}
                openaiKey={editOpenAiKey}
                anthropicKey={editAnthropicApiKey}
                openaiConfigured={editOpenAiConfigured}
                anthropicConfigured={editAnthropicConfigured}
                onEnabledChange={setEditDocumentOrderEnabled}
                onOpenAiKeyChange={setEditOpenAiKey}
                onAnthropicKeyChange={setEditAnthropicApiKey}
                disabled={editBusy || couponSaveBusy}
              />

              <div className="space-y-3 rounded-xl border border-ink/10 bg-white/40 p-4 dark:border-white/10 dark:bg-white/[0.04]">
                <p className="text-xs font-semibold text-ink dark:text-mist">Pagamento online (Mercado Pago)</p>
                <label className="flex cursor-pointer items-start gap-3 text-sm font-semibold text-ink dark:text-mist">
                  <input
                    type="checkbox"
                    checked={editPaymentOnline}
                    onChange={(e) => setEditPaymentOnline(e.target.checked)}
                    disabled={editBusy || couponSaveBusy}
                    className="mt-0.5 h-4 w-4 rounded border-ink/30 text-accent focus:ring-accent dark:border-white/25"
                  />
                  <span>Activar pagamento online para esta loja</span>
                </label>
                <label className="block text-[11px] font-semibold uppercase tracking-wide text-ink/65 dark:text-frost-muted">
                  Access Token Mercado Pago (opcional)
                  <input
                    type="password"
                    autoComplete="new-password"
                    disabled={editBusy || couponSaveBusy}
                    placeholder="vazio = não alterar"
                    className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-sm dark:border-white/10 dark:bg-[#252f2a]"
                    value={editMpAccessToken}
                    onChange={(e) => setEditMpAccessToken(e.target.value)}
                  />
                </label>
                {(Number(editModal?.mp_access_token_is_set) === 1 ||
                  Number(editModal?.mp_oauth_secret_is_set) === 1) && (
                  <p className="text-[10px] text-ink/55 dark:text-mist/72">
                    Credenciais já guardadas na base (valores ocultos). Preencha apenas para substituir.
                  </p>
                )}
                {editPaymentOnline ? (
                  <div className="flex flex-wrap items-end gap-3">
                    <label className="min-w-[12rem] flex-1 text-[11px] font-semibold uppercase tracking-wide text-ink/65 dark:text-frost-muted">
                      Rótulo da opção online no checkout (opcional)
                      <input
                        type="text"
                        maxLength={120}
                        disabled={editBusy || couponSaveBusy}
                        placeholder="Vazio = PAGAR ONLINE (MERCADO PAGO)"
                        className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                        value={editCheckoutOnlineLabel}
                        onChange={(e) => setEditCheckoutOnlineLabel(e.target.value)}
                      />
                      <span className="mt-1 block text-[10px] font-normal normal-case tracking-normal text-ink/55 dark:text-mist/72">
                        É o texto que o cliente vê no combo. Ex.:{' '}
                        <code className="rounded bg-black/10 px-1 dark:bg-white/15">PAGAR COM PIX</code> se a loja só
                        destacar Pix. Em branco, usamos o padrão.
                      </span>
                    </label>
                    <label className="w-[5.5rem] shrink-0 rounded-xl border border-emerald-500/35 bg-emerald-50/80 px-2.5 py-2 text-[11px] font-semibold uppercase tracking-wide text-emerald-950 dark:border-emerald-400/30 dark:bg-emerald-950/25 dark:text-emerald-100">
                      ID TPV
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={3}
                        disabled={editBusy || couponSaveBusy}
                        placeholder="000"
                        title="Até 3 dígitos — enviado ao ERP em cada pedido (tpv_Id)"
                        className="mt-1 w-full rounded-lg border border-emerald-600/25 bg-white px-2 py-1.5 text-center font-mono text-sm tabular-nums tracking-wider dark:border-emerald-300/20 dark:bg-[#252f2a] dark:text-mist"
                        value={editTpvId}
                        onChange={(e) => setEditTpvId(e.target.value.replace(/\D/g, '').slice(0, 3))}
                      />
                    </label>
                  </div>
                ) : null}
              </div>

              <div className="space-y-3 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 dark:border-amber-400/25 dark:bg-amber-500/10">
                <p className="text-xs font-semibold text-ink dark:text-mist">Cupons de desconto</p>
                <p className="text-[11px] text-ink/60 dark:text-frost-muted">
                  Uso único: um pedido e o código deixa de servir (sem prazo). Campanha: vários pedidos até à data máxima
                  e opcionalmente até um número máximo de usos na loja inteira — o que vier primeiro fecha o cupom.
                  Pode pausar um cupom sem apagar o código; na loja ele deixa de ser aceite enquanto estiver pausado.
                </p>
                <ul className="max-h-48 space-y-2 overflow-y-auto text-xs">
                  {discountCoupons.map((c) => {
                    const kindRaw = typeof c.kind === 'string' ? c.kind : '';
                    const isMulti = kindRaw === 'multi';
                    const uses = Number(c.uses_count) || 0;
                    const maxU = c.max_uses != null && c.max_uses !== '' ? Number(c.max_uses) : null;
                    const active = couponIsActive(c);
                    const canRemove =
                      (isMulti ? uses === 0 : !c.used_at);
                    let statusLbl = '';
                    if (isMulti) {
                      const lim = maxU != null && Number.isFinite(maxU) ? ` / ${maxU}` : '';
                      statusLbl = `${uses}${lim} usos · até ${formatYmdPt(String(c.valid_until || ''))}`;
                    } else {
                      statusLbl = c.used_at ? 'uso único — já utilizado' : 'uso único — disponível';
                    }
                    if (!active) {
                      statusLbl = `${statusLbl} · pausado`;
                    }
                    return (
                      <li
                        key={c.id}
                        className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border px-2 py-2 dark:bg-[#252f2a] ${
                          active
                            ? 'border-ink/10 bg-white/80 dark:border-white/10'
                            : 'border-ink/15 bg-ink/[0.04] opacity-90 dark:border-white/15'
                        }`}
                      >
                        <span className="font-mono font-semibold">{c.code}</span>
                        <span className="tabular-nums text-ink/75 dark:text-mist/75">
                          {Number(c.percent_discount)}%
                        </span>
                        <span
                          className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase ${isMulti ? 'bg-sky-500/20 text-sky-900 dark:text-sky-100' : 'bg-amber-500/20 text-amber-950 dark:text-amber-100'}`}
                        >
                          {isMulti ? 'campanha' : 'uso único'}
                        </span>
                        {!active ? (
                          <span className="rounded bg-ink/10 px-2 py-0.5 text-[10px] font-bold uppercase text-ink/70 dark:bg-white/10 dark:text-mist/70">
                            pausado
                          </span>
                        ) : null}
                        <span className="block w-full text-[10px] leading-snug text-ink/65 dark:text-frost-muted">
                          {statusLbl}
                        </span>
                        <span className="flex flex-wrap gap-3">
                          {active ? (
                            <button
                              type="button"
                              disabled={couponSaveBusy}
                              onClick={() => toggleDiscountCouponActive(c.id, false)}
                              className="text-[10px] font-bold uppercase text-amber-700 underline dark:text-amber-300"
                            >
                              pausar
                            </button>
                          ) : (
                            <button
                              type="button"
                              disabled={couponSaveBusy}
                              onClick={() => toggleDiscountCouponActive(c.id, true)}
                              className="text-[10px] font-bold uppercase text-emerald-700 underline dark:text-emerald-300"
                            >
                              activar
                            </button>
                          )}
                          {canRemove ? (
                            <button
                              type="button"
                              disabled={couponSaveBusy}
                              onClick={() => deleteDiscountCoupon(c.id)}
                              className="text-[10px] font-bold uppercase text-red-600 underline"
                            >
                              remover
                            </button>
                          ) : null}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                <label className="flex flex-wrap items-center gap-4 text-[11px] font-semibold text-ink dark:text-mist">
                  Tipo de cupom
                  <label className="flex cursor-pointer items-center gap-2 font-normal">
                    <input
                      type="radio"
                      checked={newCouponKind === 'single'}
                      disabled={couponSaveBusy}
                      onChange={() => setNewCouponKind('single')}
                    />
                    Uso único
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 font-normal">
                    <input
                      type="radio"
                      checked={newCouponKind === 'multi'}
                      disabled={couponSaveBusy}
                      onChange={() => setNewCouponKind('multi')}
                    />
                    Campanha (data + repetir)
                  </label>
                </label>
                {newCouponKind === 'multi' ? (
                  <div className="flex flex-wrap gap-2">
                    <label className="min-w-[10rem] text-[11px] font-semibold text-ink dark:text-mist">
                      Válido até (último dia)
                      <input
                        type="date"
                        className="mt-1 block w-full rounded-lg border border-ink/10 bg-white px-2 py-1.5 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                        value={newCouponValidUntil}
                        onChange={(e) => setNewCouponValidUntil(e.target.value)}
                        disabled={couponSaveBusy || editBusy}
                      />
                    </label>
                    <label className="min-w-[7rem] text-[11px] font-semibold text-ink dark:text-mist">
                      Máx. usos (opcional)
                      <input
                        type="number"
                        min={1}
                        placeholder="∞"
                        className="mt-1 block w-full rounded-lg border border-ink/10 bg-white px-2 py-1.5 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                        value={newCouponMaxUses}
                        onChange={(e) => setNewCouponMaxUses(e.target.value)}
                        disabled={couponSaveBusy || editBusy}
                      />
                    </label>
                  </div>
                ) : null}
                <div className="flex flex-wrap items-end gap-2">
                  <label className="min-w-[8rem] flex-1 text-[11px] font-semibold text-ink dark:text-mist">
                    Código
                    <input
                      autoComplete="off"
                      className="mt-1 w-full rounded-lg border border-ink/10 bg-white px-2 py-1.5 font-mono text-sm uppercase dark:border-white/10 dark:bg-[#252f2a]"
                      value={newCouponCode}
                      onChange={(e) => setNewCouponCode(e.target.value.toUpperCase())}
                    />
                  </label>
                  <label className="w-24 text-[11px] font-semibold text-ink dark:text-mist">
                    % (0–100)
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={0.01}
                      className="mt-1 w-full rounded-lg border border-ink/10 bg-white px-2 py-1.5 dark:border-white/10 dark:bg-[#252f2a]"
                      value={newCouponPct}
                      onChange={(e) => setNewCouponPct(e.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    disabled={couponSaveBusy || editBusy}
                    onClick={() => void submitNewCoupon()}
                    className="rounded-xl bg-ink px-3 py-2 text-xs font-semibold text-mist dark:bg-mist dark:text-ink"
                  >
                    Criar
                  </button>
                </div>
              </div>

              {editModal?.id ? (
                <AdminDeliveryZonesSection
                  tenantId={Number(editModal.id)}
                  pickupOnly={editPickupOnlyMode}
                />
              ) : null}

              <div className="border-t border-ink/10 pt-3 dark:border-white/10">
                <p className="text-xs font-semibold text-ink dark:text-mist">Logo no topo da loja</p>
              </div>
              {(logoUrlInput.trim() || editModal.logo_url) && !logoFile ? (
                <div className="flex justify-center rounded-xl border border-ink/10 bg-black/[0.03] p-4 dark:border-white/10 dark:bg-white/[0.04]">
                  <img
                    src={resolveApiMediaUrl(logoUrlInput.trim() || String(editModal.logo_url || ''))}
                    alt=""
                    className="max-h-24 max-w-full object-contain"
                  />
                </div>
              ) : null}
              <label className="block text-sm font-semibold text-ink dark:text-mist">
                URL ou caminho da imagem (opcional se enviar ficheiro)
                <input
                  type="text"
                  inputMode="url"
                  autoComplete="off"
                  spellCheck="false"
                  placeholder="https://… ou /api/uploads/…"
                  disabled={!!logoFile}
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-sm disabled:opacity-50 dark:border-white/10 dark:bg-[#252f2a]"
                  value={logoUrlInput}
                  onChange={(e) => setLogoUrlInput(e.target.value)}
                />
              </label>
              <label className="block text-sm font-semibold text-ink dark:text-mist">
                Ou ficheiro
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                  disabled={editBusy}
                  className="mt-1 block w-full text-xs text-ink/70 file:mr-3 file:rounded-xl file:border-0 file:bg-accent file:px-3 file:py-2 file:text-xs file:font-semibold file:text-white dark:text-mist/70 dark:file:bg-accent"
                  onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)}
                />
              </label>
              {logoFile ? (
                <p className="text-xs text-ink/58 dark:text-mist/72">Ao guardar, o ficheiro prevalece sobre a URL.</p>
              ) : null}

              <div className="space-y-3 border-t border-ink/10 pt-4 dark:border-white/10">
                <div className="space-y-1">
                  <h4 className="text-sm font-bold text-ink dark:text-mist">Integração com o teu ERP</h4>
                  <p className="text-[11px] leading-relaxed text-ink/65 dark:text-frost-muted">
                    O programa de sincronização no seu servidor usa a URL em <span className="font-semibold">(A)</span> e os
                    cabeçalhos em <span className="font-semibold">(B)</span>. Isto é independente do{' '}
                    <span className="font-semibold text-ink dark:text-mist">token Bearer</span> da API OminIA — esse
                    regera-se com «Regenerar token da API» na lista de empresas.
                  </p>
                </div>

                <div className="rounded-xl border border-emerald-500/35 bg-emerald-500/[0.07] px-3 py-3 dark:border-emerald-400/30 dark:bg-emerald-500/10">
                  <div className="mb-2 text-[10px] font-bold uppercase tracking-wide text-emerald-950 dark:text-emerald-50">
                    (A) URL base
                  </div>
                  <dl className="space-y-3 font-mono text-[11px] text-ink dark:text-mist">
                    <div>
                      <dt className="text-[10px] font-sans font-semibold text-ink/60 dark:text-frost-muted">
                        Endpoint (o ERP acrescenta /categorias, /produtos, …)
                      </dt>
                      <dd className="mt-1 flex flex-wrap items-start gap-2 break-all">
                        <span>{buildErpIntegrationBaseUrl()}</span>
                        <button
                          type="button"
                          disabled={editBusy}
                          className="shrink-0 rounded-lg border border-emerald-600/35 bg-white/90 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-900 dark:bg-ink dark:text-emerald-200"
                          onClick={() => copyToClipboardSnippet(buildErpIntegrationBaseUrl(), 'URL')}
                        >
                          Copiar
                        </button>
                      </dd>
                      {erpUrlLooksLikeViteDev && !import.meta.env?.VITE_ERP_INTEGRATION_URL ? (
                        <p className="mt-2 rounded-lg border border-rose-400/45 bg-rose-500/10 px-2 py-1.5 font-sans text-[10px] font-semibold leading-snug text-rose-950 dark:border-rose-400/35 dark:bg-rose-500/15 dark:text-rose-50">
                          Em <strong>localhost</strong> (ex.: porta 5173) isto não aponta para o PHP na internet — é só o{' '}
                          painel React. Para ver a URL correcta ao vivo, define no{' '}
                          <span className="font-mono">.env.development.local</span>:{' '}
                          <span className="block break-all font-mono font-normal mt-1">
                            VITE_ERP_INTEGRATION_URL=https://teu-site.com/integration-php/public/index.php/api
                          </span>{' '}
                          e reinicia o <span className="font-mono">npm run dev</span>.
                        </p>
                      ) : null}
                    </div>
                  </dl>
                  <div className="mt-4 border-t border-emerald-600/25 pt-3 dark:border-emerald-500/25">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-emerald-950 dark:text-emerald-50">
                      (B) Cabeçalhos HTTP (sincronizador)
                    </p>
                    <p className="mt-1 font-sans text-[10px] leading-snug text-ink/70 dark:text-mist/70">
                      O ERP envia estes cabeçalhos em cada pedido. Não confundir com o Bearer da API OminIA.
                    </p>
                  </div>
                  <dl className="mt-3 space-y-3 font-mono text-[11px] text-ink dark:text-mist">
                    <div>
                      <dt className="text-[10px] font-sans font-semibold text-ink/60 dark:text-frost-muted">
                        Cabeçalho X-Auth-Id (Client ID)
                      </dt>
                      <dd className="mt-1 flex flex-wrap items-start gap-2 break-all">
                        <span>{editErpClientId.trim() !== '' ? editErpClientId.trim() : '— defina ao gravar ou use o SQL manual'}</span>
                        {editErpClientId.trim() !== '' ? (
                          <button
                            type="button"
                            disabled={editBusy}
                            className="shrink-0 rounded-lg border border-emerald-600/35 bg-white/90 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-900 dark:bg-ink dark:text-emerald-200"
                            onClick={() => copyToClipboardSnippet(editErpClientId.trim(), 'Client ID')}
                          >
                            Copiar
                          </button>
                        ) : null}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[10px] font-sans font-semibold text-ink/60 dark:text-frost-muted">
                        Cabeçalho X-Auth-Secret (secret gravado na base)
                      </dt>
                      <dd className="mt-1 space-y-2">
                        {editErpClientSecret.trim() !== '' ? (
                          <p className="font-sans text-[10px] font-semibold text-amber-800 dark:text-amber-200">
                            Nova secret está no campo em baixo: será gravada ao clicar Guardar (até lá, usa o texto
                            abaixo se já existia um segredo antes).
                          </p>
                        ) : null}
                        {erpStoredSecretDisplay !== '' ? (
                          <div className="flex flex-wrap items-start gap-2 rounded-lg border border-emerald-600/25 bg-black/[0.04] px-2 py-2 dark:bg-white/[0.06]">
                            <code className="flex-1 min-w-[12rem] break-all text-[11px] text-ink dark:text-mist">
                              {erpStoredSecretDisplay}
                            </code>
                            <button
                              type="button"
                              disabled={editBusy}
                              className="shrink-0 rounded-lg border border-emerald-600/35 bg-white/90 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-900 dark:bg-ink dark:text-emerald-200"
                              onClick={() =>
                                copyToClipboardSnippet(erpStoredSecretDisplay, 'X-Auth-Secret')
                              }
                            >
                              Copiar
                            </button>
                          </div>
                        ) : (
                          <p className="font-sans text-[10px] text-ink/65 dark:text-frost-muted">
                            Ainda <strong>não há</strong> secret na base — pode defini-lo pelo SQL / phpMyAdmin ou preencher
                            Client Secret mais abaixo e clicar Guardar (com conta Ouro). O servidor devolve este valor
                            para facilitar configurar o ERP nesta fase.
                          </p>
                        )}
                        <p className="font-sans text-[10px] text-ink/55 dark:text-mist/72">
                          Trate isto como password: só partilhe com o programa de sincronização de confiança.
                        </p>
                      </dd>
                    </div>
                  </dl>
                </div>

                {!canSaveErp ? (
                  <p className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] font-semibold text-amber-950 dark:border-amber-400/35 dark:bg-amber-500/15 dark:text-amber-50">
                    Os campos abaixo aparecem para todos; só é possível <strong>gravar</strong> com conta{' '}
                    <strong>Plano Ouro</strong> (ou super admin). No trial, a API recusa guardar estas credenciais.
                  </p>
                ) : null}
                <label className="block text-sm font-semibold text-ink dark:text-mist">
                  Client ID (X-Auth-Id)
                  <input
                    type="text"
                    autoComplete="off"
                    spellCheck="false"
                    disabled={editBusy || !canSaveErp}
                    className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-sm disabled:opacity-50 dark:border-white/10 dark:bg-[#252f2a]"
                    value={editErpClientId}
                    onChange={(e) => setEditErpClientId(e.target.value)}
                    placeholder="ex.: identificador que o ERP envia"
                  />
                </label>
                <label className="block text-sm font-semibold text-ink dark:text-mist">
                  Client Secret (X-Auth-Secret)
                  <input
                    type="password"
                    autoComplete="new-password"
                    disabled={editBusy || !canSaveErp}
                    className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-sm disabled:opacity-50 dark:border-white/10 dark:bg-[#252f2a]"
                    value={editErpClientSecret}
                    onChange={(e) => setEditErpClientSecret(e.target.value)}
                    placeholder="deixe em branco para não alterar o segredo atual"
                  />
                </label>
                <p className="text-[11px] font-semibold text-ink/80 dark:text-mist/80">(C) Rotação do segredo</p>
                <p className="mt-0.5 text-[11px] leading-relaxed text-ink/55 dark:text-frost-muted">
                  «Limpar segredo ERP» remove o <span className="font-mono">X-Auth-Secret</span> guardado na base: o
                  sincronizador deixa de autenticar até gravar um novo segredo (o Client ID mantém-se se não o apagar).
                  Para alterar o par completo, edite os campos acima e use <span className="font-semibold">Guardar</span>.
                </p>
                <button
                  type="button"
                  disabled={editBusy || !canSaveErp}
                  title="Remove o X-Auth-Secret na base. O ERP deixa de autenticar até configurar um novo segredo e gravar. Não invalida o token Bearer da API OminIA."
                  onClick={async () => {
                      if (!editModal) return;
                      setEditError('');
                      setEditBusy(true);
                      try {
                        const res = await api(`/tenants/${editModal.id}/settings`, {
                          method: 'PATCH',
                          body: JSON.stringify({ erp_client_secret: null }),
                        });
                        setEditErpClientSecret('');
                        if (res?.tenant) {
                          setEditModal((prev) =>
                            prev && prev.id === editModal.id ? { ...prev, ...res.tenant } : prev
                          );
                        }
                        await reload();
                      } catch (err) {
                        setEditError(err.message);
                      } finally {
                        setEditBusy(false);
                      }
                    }}
                    className="rounded-xl border border-ink/15 px-3 py-2 text-xs font-semibold text-ink/80 hover:border-accent2 hover:text-accent2 dark:border-white/20 dark:text-mist/85"
                  >
                    Limpar segredo ERP (X-Auth-Secret)
                  </button>
                </div>
                </div>
              )}

              {editError ? <p className="text-sm font-semibold text-accent2">{editError}</p> : null}
              <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:flex-wrap">
                <button
                  type="submit"
                  disabled={editBusy}
                  className="w-full justify-center rounded-2xl bg-accent px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50 sm:w-auto"
                >
                  {editBusy ? 'A guardar…' : 'Guardar'}
                </button>
                <button
                  type="button"
                  disabled={editBusy || !(editModal.logo_url || logoUrlInput.trim())}
                  onClick={() => removeLogo()}
                  className="w-full justify-center rounded-2xl border border-accent2/40 px-5 py-2.5 text-sm font-semibold text-accent2 disabled:opacity-40 sm:w-auto"
                >
                  Remover logo
                </button>
                <button
                  type="button"
                  disabled={editBusy}
                  onClick={() => {
                    closeEditModal();
                  }}
                  className="w-full justify-center rounded-2xl border border-ink/15 px-5 py-2.5 text-sm font-semibold dark:border-white/20 sm:w-auto"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
